import { useState, useEffect, useCallback, useRef } from 'react'
import {
  ChevronDown, ChevronUp, ChevronRight, Eye, EyeOff,
  Info, Wifi, WifiOff, RefreshCw, X,
  Shield, Building2, Fingerprint, IdCard, Copy, Check, Radio, Trash2,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { RECIPES } from './components/HelpDocBuilder/data/v1/recipes'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Creds {
  customerId: string
  childCustomerId: string
  apiKey: string
  apiBase: string
  machineBase: string
}

const DEFAULT_CREDS: Creds = {
  customerId: '',
  childCustomerId: '',
  apiKey: '',
  apiBase: 'https://api.uat.frankie.one/v2',
  machineBase: 'https://backend.kycaml.uat.frankiefinancial.io',
}

interface LogEntry {
  id: number
  ts: string
  method: string
  url: string
  status: number
  durationMs: number
  requestBody?: unknown
  responseBody?: unknown
  annotation: string
  isMock?: boolean
}

interface IndividualForm {
  givenName: string; middleName: string; familyName: string
  dobYear: string; dobMonth: string; dobDay: string
  nationality: string
  addressStreetNumber: string; addressStreetName: string; addressStreetType: string
  addressLocality: string; addressSubdivision: string; addressPostalCode: string; addressCountry: string
  docType: string; docNumber: string; docSecondaryIdentifier: string; docCountry: string; docSubdivision: string
  consentGeneral: boolean; consentDocs: boolean; externalRef: string
}

const BLANK_FORM: IndividualForm = {
  givenName: '', middleName: '', familyName: '',
  dobYear: '', dobMonth: '', dobDay: '',
  nationality: 'AUS',
  addressStreetNumber: '', addressStreetName: '', addressStreetType: '',
  addressLocality: '', addressSubdivision: '', addressPostalCode: '', addressCountry: 'AUS',
  docType: 'PASSPORT', docNumber: '', docSecondaryIdentifier: '', docCountry: 'AUS', docSubdivision: '',
  consentGeneral: true, consentDocs: true, externalRef: '',
}

function buildIndividual(form: IndividualForm) {
  const doc: Record<string, string> = {
    type: form.docType, class: 'IDENTITY', primaryIdentifier: form.docNumber, country: form.docCountry,
  }
  if (form.docSecondaryIdentifier.trim()) doc.secondaryIdentifier = form.docSecondaryIdentifier.trim()
  if (form.docType === 'DRIVERS_LICENSE' && form.docSubdivision.trim()) doc.subdivision = form.docSubdivision.trim()
  return {
    individual: {
      name: { givenName: form.givenName, ...(form.middleName.trim() ? { middleName: form.middleName.trim() } : {}), familyName: form.familyName },
      dateOfBirth: { year: form.dobYear, month: form.dobMonth, day: form.dobDay },
      nationality: form.nationality,
      addresses: [{ type: 'RESIDENTIAL', streetNumber: form.addressStreetNumber, streetName: form.addressStreetName, streetType: form.addressStreetType, locality: form.addressLocality, subdivision: form.addressSubdivision, postalCode: form.addressPostalCode, country: form.addressCountry }],
      documents: { IDENTITY: [doc] },
      consents: ([form.consentGeneral ? { type: 'GENERAL' } : null, form.consentDocs ? { type: 'DOCS' } : null] as ({ type: string } | null)[]).filter((x): x is { type: string } => x !== null),
      ...(form.externalRef ? { externalReferences: [{ type: 'CUSTOMER', name: 'customer-id', value: form.externalRef }] } : {}),
    },
  }
}

// ── KYC Scenarios ─────────────────────────────────────────────────────────────

const STD_CONSENTS = [{ type: 'GENERAL' }, { type: 'DOCS' }, { type: 'CREDITHEADER' }, { type: 'UNDER18' }]

interface KycScenario {
  label: string
  expectedResult: 'PASS' | 'FAIL' | 'REVIEW'
  category: 'Australian' | 'International'
  rawPayload: { individual: Record<string, unknown> }
}

const KYC_SCENARIOS: KycScenario[] = [
  { label: "PASS — JAMES TESTONE (Driver's License)", expectedResult: 'PASS', category: 'Australian', rawPayload: { individual: { name: { givenName: 'JAMES', middleName: 'A', familyName: 'TESTONE' }, dateOfBirth: { year: '1950', month: '01', day: '01' }, addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', unitNumber: 'U 1', streetNumber: '35', streetName: 'CONN', streetType: 'STREET', locality: 'FERNTREE GULLY', subdivision: 'VIC', postalCode: '3156', country: 'AUS' }], documents: { IDENTITY: [{ type: 'DRIVERS_LICENSE', class: 'IDENTITY', primaryIdentifier: '283229690', secondaryIdentifier: 'P5403241', country: 'AUS', subdivision: 'VIC' }] }, consents: STD_CONSENTS } } },
  { label: "FAIL — JENNY TESTEIGHT (Driver's License)", expectedResult: 'FAIL', category: 'Australian', rawPayload: { individual: { name: { givenName: 'JENNY', familyName: 'TESTEIGHT' }, dateOfBirth: { year: '1957', month: '01', day: '01' }, addresses: [{ type: 'RESIDENTIAL', streetNumber: '56', streetName: 'VICTORIA', streetType: 'STREET', locality: 'FINGAL', subdivision: 'TAS', postalCode: '7214', country: 'AUS' }], documents: { IDENTITY: [{ type: 'DRIVERS_LICENSE', class: 'IDENTITY', primaryIdentifier: 'T56543', secondaryIdentifier: 'T03989853', country: 'AUS', subdivision: 'TAS' }] }, consents: STD_CONSENTS } } },
  { label: 'PASS — PETER TESTTHIRTEEN (Passport)', expectedResult: 'PASS', category: 'Australian', rawPayload: { individual: { name: { givenName: 'PETER', familyName: 'TESTTHIRTEEN' }, dateOfBirth: { year: '1962', month: '01', day: '01' }, nationality: 'AUS', addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', streetNumber: '19', streetName: 'LIBANANGU', streetType: 'ROAD', locality: 'KALKARINDJI', subdivision: 'NT', postalCode: '0852', country: 'AUS' }], documents: { IDENTITY: [{ type: 'PASSPORT', class: 'IDENTITY', primaryIdentifier: 'P77864526', country: 'AUS' }] }, consents: STD_CONSENTS } } },
  { label: 'REVIEW — PEP — JAMES G TESTELEVEN', expectedResult: 'REVIEW', category: 'Australian', rawPayload: { individual: { name: { givenName: 'JAMES', middleName: 'G', familyName: 'TESTELEVEN' }, dateOfBirth: { year: '1960', month: '01', day: '01' }, addresses: [{ type: 'RESIDENTIAL', streetNumber: '23', streetName: 'ISA', streetType: 'STREET', locality: 'FYSHWICK', subdivision: 'ACT', postalCode: '2609', country: 'AUS' }], documents: { IDENTITY: [{ type: 'PASSPORT', class: 'IDENTITY', primaryIdentifier: 'E55173628', country: 'AUS', expiryDate: { normalized: '2030-01-01', year: '2030', month: '01', day: '01', type: 'GREGORIAN' } }] }, consents: STD_CONSENTS } } },
  { label: 'REVIEW — Sanction — BRUCE TESTNINETEEN', expectedResult: 'REVIEW', category: 'Australian', rawPayload: { individual: { name: { givenName: 'BRUCE', familyName: 'TESTNINETEEN' }, dateOfBirth: { year: '1968', month: '01', day: '01' }, addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', streetNumber: '12', streetName: 'THROSSELL', streetType: 'STREET', locality: 'CURTIN', subdivision: 'ACT', postalCode: '2605', country: 'AUS' }], documents: { IDENTITY: [{ type: 'PASSPORT', class: 'IDENTITY', primaryIdentifier: 'P32516383', country: 'AUS' }] }, consents: STD_CONSENTS } } },
  { label: 'REVIEW — PEP+Sanction — STACY K TESTTWENTY', expectedResult: 'REVIEW', category: 'Australian', rawPayload: { individual: { name: { givenName: 'STACY', middleName: 'K', familyName: 'TESTTWENTY' }, dateOfBirth: { year: '1969', month: '01', day: '01' }, addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', streetNumber: '27', streetName: 'MILMAN', streetType: 'STREET', locality: 'MARLBOROUGH', subdivision: 'QLD', postalCode: '4705', country: 'AUS' }], documents: { IDENTITY: [{ type: 'PASSPORT', class: 'IDENTITY', primaryIdentifier: 'P19031137', country: 'AUS' }] }, consents: STD_CONSENTS } } },
  { label: 'PASS — MARY TESTFOUR (Medicare)', expectedResult: 'PASS', category: 'Australian', rawPayload: { individual: { name: { givenName: 'MARY', familyName: 'TESTFOUR' }, dateOfBirth: { year: '1953', month: '01', day: '01' }, addresses: [{ type: 'RESIDENTIAL', streetNumber: '17', streetName: 'WATTLE', streetType: 'AVENUE', locality: 'WARWICK', subdivision: 'QLD', postalCode: '4370', country: 'AUS' }], documents: { IDENTITY: [{ type: 'NATIONAL_HEALTH_ID', class: 'IDENTITY', primaryIdentifier: '2980366171', subtype: 'B', country: 'AUS', subdivision: 'SA', expiryDate: { year: '2030', month: '01', day: '01', unstructured: '2030-01-01', type: 'GREGORIAN' }, supplementaryData: { type: 'NATIONAL_HEALTH_ID', reference: '2' } }] }, consents: STD_CONSENTS } } },
]

// ── KYB Scenarios ─────────────────────────────────────────────────────────────

interface KybScenario {
  label: string
  searchBy: 'registration'
  searchRegType: 'ABN' | 'ACN'
  searchValue: string
  name: string
  abn?: string
  acn?: string
  tags: string[]
  comment?: string
  entityId?: string
}

const KYB_SCENARIOS: KybScenario[] = [
  { label: 'FOX PTY. LTD. — Fast (~30s)', searchBy: 'registration', searchRegType: 'ACN', searchValue: '052121347', name: 'FOX PTY LTD', acn: '052121347', tags: ['Fast'] },
  { label: 'BCP (PITT STREET) PTY LTD — Medium (~1-2 min)', searchBy: 'registration', searchRegType: 'ACN', searchValue: '650193865', name: 'BCP PITT STREET', acn: '650193865', tags: ['Medium'] },
  { label: 'CLARO DISABILITY SERVICES PTY LTD — Complex (~5 min)', searchBy: 'registration', searchRegType: 'ACN', searchValue: '082554630', name: 'CLARO DISABILITY', acn: '082554630', tags: ['Complex UBO'] },
  { label: 'BEACON LIGHTING GROUP LIMITED', searchBy: 'registration', searchRegType: 'ACN', searchValue: '164122785', name: 'BEACON LIGHTING', acn: '164122785', abn: '90164122785', tags: ['ASIC Profile', 'Complex UBO'] },
]

// ── Hosted Flows ──────────────────────────────────────────────────────────────

const HOSTED_FLOWS = [
  { id: 'idv_daon', label: 'Biometrics/OCR — Daon' },
  { id: 'idv', label: 'Biometrics/OCR' },
  { id: 'idv_kyc', label: 'eKYC + Biometrics/OCR' },
  { id: 'manual_kyc', label: 'eKYC (form only)' },
  { id: 'doc_upload', label: 'Document Upload' },
  { id: 'ocr_only', label: 'OCR Only' },
  { id: 'idv_onfido', label: 'Biometrics/OCR — Onfido' },
  { id: 'idv_sumsub', label: 'Biometrics/OCR — Sumsub' },
  { id: 'default', label: 'default' },
]

// ── Embedded Flows ────────────────────────────────────────────────────────────

const EMBEDDED_FLOWS = [
  { id: 'idv', label: 'IDV — Document + Biometrics', note: 'Uses instance.flow("idv"). Biometrics provider set in portal.' },
  { id: 'manual_kyc', label: 'eKYC — Manual form entry', note: 'Chains WELCOME → CONSENT → PERSONAL → DOCUMENT → REVIEW. No flow() call.' },
]

function isEkycFlow(flowId: string) { return flowId.startsWith('manual_kyc') }

// ── Shared helpers ─────────────────────────────────────────────────────────────

function resultColor(r?: string) {
  if (!r) return 'text-[var(--text-muted)]'
  if (r === 'PASS' || r === 'CLEAR') return 'text-emerald-400'
  if (r === 'FAIL') return 'text-red-400'
  return 'text-amber-400'
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'CLEAR' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
    : status === 'PASS' || status === 'COMPLETE' || status === 'COMPLETED' ? 'bg-green-500/15 text-green-400 border-green-500/30'
    : status === 'FAIL' ? 'bg-red-500/15 text-red-400 border-red-500/30'
    : status === 'PROCESSING' || status === 'PENDING' ? 'bg-blue-500/15 text-blue-400 border-blue-500/30'
    : 'bg-amber-500/15 text-amber-400 border-amber-500/30'
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border ${cls}`}>{status}</span>
}

function MethodBadge({ method }: { method: string }) {
  const cls = method === 'GET' ? 'bg-sky-500/15 text-sky-300' : method === 'POST' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-gray-500/15 text-gray-300'
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold shrink-0 ${cls}`}>{method}</span>
}

function HttpBadge({ code }: { code: number }) {
  const cls = code === 202 ? 'bg-amber-500/15 text-amber-300' : code >= 200 && code < 300 ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold shrink-0 ${cls}`}>{code}</span>
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button onClick={() => { void navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border-color)] transition-colors">
      {copied ? <Check size={10} className="text-green-400" /> : <Copy size={10} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

function JsonView({ data }: { data: unknown }) {
  const json = JSON.stringify(data, null, 2)
  const TOKEN = /("(?:[^"\\]|\\.)*")\s*(:)?|(\btrue\b|\bfalse\b|\bnull\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g
  const nodes: React.ReactNode[] = []; let last = 0; let m: RegExpExecArray | null; let key = 0
  while ((m = TOKEN.exec(json)) !== null) {
    if (m.index > last) nodes.push(<span key={key++}>{json.slice(last, m.index)}</span>)
    const [, str, colon, keyword, number] = m
    if (str !== undefined) {
      if (colon) { nodes.push(<span key={key++} className="text-sky-300">{str}</span>); nodes.push(<span key={key++} className="text-gray-400">:</span>) }
      else nodes.push(<span key={key++} className="text-amber-300">{str}</span>)
    } else if (keyword !== undefined) nodes.push(<span key={key++} className={keyword === 'true' ? 'text-green-400' : keyword === 'false' ? 'text-red-400' : 'text-gray-500'}>{keyword}</span>)
    else if (number !== undefined) nodes.push(<span key={key++} className="text-violet-300">{number}</span>)
    else nodes.push(<span key={key++} className="text-gray-400">{m[0]}</span>)
    last = m.index + m[0].length
  }
  if (last < json.length) nodes.push(<span key={key++}>{json.slice(last)}</span>)
  return <pre className="text-[10px] font-mono leading-relaxed whitespace-pre-wrap overflow-x-auto">{nodes}</pre>
}

// ── Workflow result components ────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function WorkflowSteps({ steps }: { steps: any }) {
  const order: string[] = steps?.order ?? []
  const passed: string[] = steps?.passed ?? []
  const failed: string[] = steps?.failed ?? []
  return (
    <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-2">
      <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">workflowResult.steps</p>
      <div className="flex items-center gap-1 flex-wrap">
        {order.map((s, i) => {
          const ok = passed.includes(s); const fail = failed.includes(s)
          return (
            <div key={i} className="flex items-center gap-1">
              <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-medium ${fail ? 'bg-red-500/15 text-red-400' : ok ? 'bg-emerald-500/15 text-emerald-400' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'}`}>
                {ok ? '✓ ' : fail ? '✗ ' : ''}{s}
              </span>
              {i < order.length - 1 && <span className="text-[var(--text-muted)] text-[10px]">→</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function WorkflowStepResults({ stepResults }: { stepResults: any[] }) {
  return (
    <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-2">
      <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">workflowResult.workflowStepResults[]</p>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {stepResults.map((step: any, i: number) => (
        <div key={i} className="border border-[var(--border-color)] rounded p-2.5 space-y-1.5 bg-[var(--bg-primary)]">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono font-semibold text-[var(--text-secondary)]">{step.stepName}</span>
            {step.result && <StatusBadge status={step.result} />}
          </div>
          {step.processResults?.length > 0 && (
            <div className="ml-2 border-l-2 border-[var(--border-color)] pl-2.5 space-y-1">
              <span className="text-[9px] font-mono text-[var(--text-muted)]">processResults[]</span>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {step.processResults.map((pr: any, j: number) => (
                <div key={j} className="flex items-center gap-1.5 text-[10px] font-mono flex-wrap">
                  <span className="text-[var(--text-secondary)]">{pr.class}</span>
                  {pr.objectType && <span className="text-[var(--text-muted)]">({pr.objectType})</span>}
                  {pr.result && <StatusBadge status={pr.result} />}
                  {pr.state && <span className="text-[var(--text-muted)]">state: {pr.state}</span>}
                  {pr.providerResult?.source && <span className="text-[var(--text-muted)]">via {pr.providerResult.source}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function RiskAssessment({ risk }: { risk: any }) {
  return (
    <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-1.5">
      <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">workflowResult.riskAssessment</p>
      <div className="flex items-center gap-2 text-[10px]">
        <span className="text-[var(--text-muted)] font-mono">riskLevel:</span>
        {risk.riskLevel && <StatusBadge status={risk.riskLevel} />}
      </div>
      {risk.riskScore !== undefined && (
        <div className="flex items-center gap-2 text-[10px]">
          <span className="text-[var(--text-muted)] font-mono">riskScore:</span>
          <span className="text-[var(--text-primary)] font-semibold font-mono">{risk.riskScore}</span>
        </div>
      )}
      {risk.riskFactors?.length > 0 && (
        <div className="ml-2 border-l-2 border-[var(--border-color)] pl-2.5 space-y-1 mt-1">
          <span className="text-[9px] font-mono text-[var(--text-muted)]">riskFactors[]</span>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {risk.riskFactors.map((rf: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-[10px] font-mono">
              <span className="text-[var(--text-secondary)]">{rf.factor}</span>
              <span className="text-[var(--text-muted)]">→</span>
              <span className="text-[var(--text-primary)] font-semibold">{rf.score}</span>
              {rf.description && <span className="text-[var(--text-muted)]">({rf.description})</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function Issues({ issues }: { issues: any[] }) {
  return (
    <div className="border border-orange-500/30 rounded-lg p-3 bg-orange-500/5 space-y-1">
      <p className="text-[10px] font-semibold text-orange-400 uppercase tracking-wide">workflowResult.issues[]</p>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {issues.map((issue: any, i: number) => (
        <p key={i} className="text-[10px] font-mono text-orange-400">{issue.code}: {issue.description}</p>
      ))}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function WorkflowSummary({ wr, expectedResult }: { wr: any; expectedResult?: string }) {
  return (
    <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-1.5">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">workflowResult</p>
        {expectedResult && (
          <span className={`text-[10px] font-bold ${resultColor(expectedResult)}`}>Expected: {expectedResult}</span>
        )}
      </div>
      {[['workflowExecutionId', wr.workflowExecutionId], ['workflowExecutionState', wr.workflowExecutionState], ['status', wr.status], ['result', wr.result]].map(([k, v]) => (
        v !== undefined && v !== null ? (
          <div key={k} className="flex items-center gap-2 text-[10px]">
            <span className="text-[var(--text-muted)] font-mono w-40 shrink-0">{k}:</span>
            {typeof v === 'string' && ['PASS','FAIL','REVIEW','CLEAR','COMPLETE','COMPLETED','ERROR','TIMEOUT','PROCESSING'].includes(v)
              ? <StatusBadge status={v} />
              : <span className="text-[var(--text-secondary)] font-mono">{String(v)}</span>
            }
          </div>
        ) : null
      ))}
    </div>
  )
}

// ── Fraud step result (Hosted tab) ────────────────────────────────────────────

const FRAUD_CHECK_LABELS: Record<string, string> = {
  FRAUD_DEVICE: 'Device',
  FRAUD_IP_ADDRESS: 'IP Address',
  FRAUD_EMAIL_ADDRESS: 'Email',
  FRAUD_PHONE_NUMBER: 'Phone',
}

const RISK_LEVEL_CLS: Record<string, string> = {
  LOW: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  MEDIUM: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  HIGH: 'bg-red-500/15 text-red-400 border-red-500/30',
  UNACCEPTABLE: 'bg-red-700/20 text-red-300 border-red-600/40',
}

function RiskLevelBadge({ level }: { level: string }) {
  const cls = RISK_LEVEL_CLS[level] ?? 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border-[var(--border-color)]'
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border ${cls}`}>{level}</span>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function FraudStepSection({ wr }: { wr: any }) {
  const fraudStep = (wr?.workflowStepResults ?? []).find((s: any) => s.stepName === 'FRAUD')  // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!fraudStep) return null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const checks: any[] = fraudStep.processResults ?? []
  if (checks.length === 0) return null

  return (
    <div className="border border-violet-500/30 rounded-lg p-3 bg-violet-500/5 space-y-3">
      <div className="flex items-center gap-2">
        <Shield size={11} className="text-violet-400" />
        <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-wide">Fraud / Device Risk</p>
        {fraudStep.result && <StatusBadge status={fraudStep.result} />}
      </div>
      <div className="space-y-2">
        {checks.map((check: any, i: number) => {  // eslint-disable-line @typescript-eslint/no-explicit-any
          const label = FRAUD_CHECK_LABELS[check.class] ?? check.class
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const suppData: Record<string, string> = check.supplementaryData ?? check.providerResult?.supplementaryData ?? {}
          const riskLevel: string | undefined = check.riskLevel ?? suppData['Risk.Level']
          return (
            <div key={i} className="border border-[var(--border-color)] rounded p-2.5 space-y-1.5 bg-[var(--bg-primary)]">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono font-semibold text-[var(--text-secondary)]">{label}</span>
                <span className="text-[10px] font-mono text-[var(--text-muted)]">({check.class})</span>
                {check.result && <StatusBadge status={check.result} />}
                {riskLevel && <RiskLevelBadge level={riskLevel} />}
              </div>
              {check.class === 'FRAUD_DEVICE' && Object.keys(suppData).length > 0 && (
                <table className="w-full text-[10px]">
                  <tbody>
                    {Object.entries(suppData).map(([k, v]) => (
                      <tr key={k} className="border-t border-[var(--border-color)] first:border-0">
                        <td className="py-0.5 pr-3 font-mono text-[var(--text-muted)] w-44">{k}</td>
                        <td className="py-0.5 font-mono">
                          {v === 'true' ? <span className="text-red-400 font-bold">{v}</span>
                            : v === 'false' ? <span className="text-emerald-400">{v}</span>
                            : v === 'HIGH' || v === 'UNACCEPTABLE' ? <span className="text-red-400 font-bold">{v}</span>
                            : v === 'MEDIUM' ? <span className="text-amber-400">{v}</span>
                            : v === 'LOW' || v === 'GOOD' ? <span className="text-emerald-400">{v}</span>
                            : <span className="text-[var(--text-secondary)]">{v}</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function KycResultSummary({ wr, fullResponse, expectedResult }: { wr: any; fullResponse: unknown; expectedResult?: string }) {
  const [rawOpen, setRawOpen] = useState(false)
  return (
    <div className="space-y-2">
      {wr.steps && <WorkflowSteps steps={wr.steps} />}
      {wr.workflowStepResults?.length > 0 && <WorkflowStepResults stepResults={wr.workflowStepResults} />}
      {wr.issues?.length > 0 && <Issues issues={wr.issues} />}
      {wr.riskAssessment && <RiskAssessment risk={wr.riskAssessment} />}
      <WorkflowSummary wr={wr} expectedResult={expectedResult} />
      <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
        <button onClick={() => setRawOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-tertiary)] transition-colors">
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Raw JSON Response</span>
          {rawOpen ? <ChevronUp size={10} className="text-[var(--text-muted)]" /> : <ChevronDown size={10} className="text-[var(--text-muted)]" />}
        </button>
        {rawOpen && (
          <div className="border-t border-[var(--border-color)] bg-[var(--bg-primary)] p-2 max-h-64 overflow-y-auto">
            <JsonView data={fullResponse} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Log Panel ─────────────────────────────────────────────────────────────────

function LogEntryModal({ entry, onClose }: { entry: LogEntry; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--border-color)] flex-shrink-0">
          <MethodBadge method={entry.method} />
          <HttpBadge code={entry.status} />
          <code className="text-[10px] font-mono text-[var(--text-secondary)] truncate flex-1">{entry.url.replace(/https?:\/\/[^/]+/, '')}</code>
          <span className="text-[9px] text-[var(--text-muted)] flex-shrink-0">{entry.durationMs}ms</span>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] flex-shrink-0 ml-1"><X size={14} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          <p className="text-[10px] text-[var(--text-muted)] italic">{entry.annotation}</p>
          {entry.requestBody !== undefined && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Request Body</p>
                <CopyBtn text={JSON.stringify(entry.requestBody, null, 2)} />
              </div>
              <div className="bg-[var(--bg-primary)] rounded p-2.5 border border-[var(--border-color)]"><JsonView data={entry.requestBody} /></div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Response</p>
                <HttpBadge code={entry.status} />
              </div>
              <CopyBtn text={JSON.stringify(entry.responseBody, null, 2)} />
            </div>
            <div className="bg-[var(--bg-primary)] rounded p-2.5 border border-[var(--border-color)]"><JsonView data={entry.responseBody} /></div>
          </div>
        </div>
      </div>
    </div>
  )
}

function LogPanel({ log, onClear }: { log: LogEntry[]; onClear: () => void }) {
  const [selected, setSelected] = useState<number | null>(null)
  const entry = selected !== null ? log.find(e => e.id === selected) ?? null : null
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-color)] flex-shrink-0">
        <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">API Log</span>
        {log.length > 0 && <button onClick={onClear} className="text-[10px] text-[var(--text-muted)] hover:text-red-400 transition-colors">Clear</button>}
      </div>
      <div className="overflow-y-auto flex-1">
        {log.length === 0 && <p className="px-3 py-4 text-[10px] text-[var(--text-muted)]">No calls yet.</p>}
        {[...log].reverse().map(e => (
          <button key={e.id} onClick={() => setSelected(e.id)}
            className="w-full text-left px-3 py-2 border-b border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] transition-colors">
            <div className="flex items-center gap-1.5 mb-0.5"><MethodBadge method={e.method} /><HttpBadge code={e.status} /></div>
            <p className="text-[10px] text-[var(--text-secondary)] truncate font-mono">{e.url.replace(/https?:\/\/[^/]+/, '')}</p>
            <p className="text-[9px] text-[var(--text-muted)] mt-0.5">{e.ts} · {e.durationMs}ms</p>
          </button>
        ))}
      </div>
      {entry && <LogEntryModal entry={entry} onClose={() => setSelected(null)} />}
    </div>
  )
}

// ── Credentials Panel ─────────────────────────────────────────────────────────

interface SavedConfig {
  id: number; name: string
  customer_id: string; child_customer_id: string | null
  api_key: string; api_base: string; machine_base: string
}

function CredentialsPanel({ creds, onChange }: { creds: Creds; onChange: (c: Creds) => void }) {
  const [open, setOpen] = useState(false)
  const [showKey, setShowKey] = useState(false)
  const [configs, setConfigs] = useState<SavedConfig[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)  // which saved config is loaded
  const [saveName, setSaveName] = useState('')
  const [saving, setSaving] = useState(false)
  const configured = !!(creds.customerId && creds.apiKey)
  const activeConfig = configs.find(c => c.id === activeId) ?? null

  useEffect(() => { fetchConfigs() }, [])

  async function fetchConfigs() {
    try {
      const res = await fetch('/api/api-console/credentials')
      const json = await res.json() as { success: boolean; data: SavedConfig[] }
      if (json.success) setConfigs(json.data)
    } catch { /* silent */ }
  }

  async function saveNew() {
    if (!saveName.trim() || !creds.customerId || !creds.apiKey) return
    setSaving(true)
    try {
      const res = await fetch('/api/api-console/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: saveName.trim(), customerId: creds.customerId, childCustomerId: creds.childCustomerId, apiKey: creds.apiKey, apiBase: creds.apiBase, machineBase: creds.machineBase }),
      })
      const json = await res.json() as { success: boolean; data: { id: number } }
      setSaveName('')
      await fetchConfigs()
      if (json.success) setActiveId(json.data.id)
    } finally { setSaving(false) }
  }

  async function deleteConfig(id: number) {
    await fetch(`/api/api-console/credentials/${id}`, { method: 'DELETE' })
    setConfigs(prev => prev.filter(c => c.id !== id))
    if (activeId === id) setActiveId(null)
  }

  function selectConfig(id: number) {
    const c = configs.find(x => x.id === id)
    if (!c) return
    setActiveId(id)
    onChange({ customerId: c.customer_id, childCustomerId: c.child_customer_id ?? '', apiKey: c.api_key, apiBase: c.api_base, machineBase: c.machine_base })
  }

  function clearConfig() { setActiveId(null); onChange(DEFAULT_CREDS) }

  const inputCls = 'w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-primary'

  return (
    <div className="border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] overflow-hidden">

      {/* Header — always visible */}
      <div className="px-3 py-2 space-y-2">
        {/* Config selector row */}
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-[var(--text-secondary)] shrink-0">Credentials</span>
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${configured ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
          <div className="flex-1 min-w-0">
            <select
              className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary"
              value={activeId ?? ''}
              onChange={e => { if (e.target.value === '') clearConfig(); else selectConfig(parseInt(e.target.value)) }}
            >
              <option value="">— {configs.length ? 'Select a configuration' : 'No saved configurations'} —</option>
              {configs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {activeId && (
            <button onClick={() => deleteConfig(activeId)} title="Delete this configuration" className="shrink-0 text-[var(--text-muted)] hover:text-red-400 transition-colors">
              <X size={12} />
            </button>
          )}
          <button onClick={() => setOpen(o => !o)} className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>

        {/* Active config name hint */}
        {activeConfig && !open && (
          <p className="text-[10px] text-[var(--text-muted)] font-mono truncate">{activeConfig.customer_id}</p>
        )}
      </div>

      {/* Expanded fields */}
      {open && (
        <div className="px-3 pb-3 border-t border-[var(--border-color)] space-y-3 pt-2.5">

          {/* Credential fields */}
          <div className="space-y-2">
            {([
              { key: 'customerId' as const, label: 'Customer ID', ph: 'your-customer-id', required: true },
              { key: 'childCustomerId' as const, label: 'Child Customer ID', ph: 'optional', required: false },
              { key: 'apiKey' as const, label: 'API Key', ph: 'your-api-key', required: true },
              { key: 'apiBase' as const, label: 'API Base URL', ph: 'https://api.uat.frankie.one/v2', required: false },
              { key: 'machineBase' as const, label: 'Machine Base URL', ph: 'https://backend.kycaml.uat.frankiefinancial.io', required: false },
            ] as const).map(f => (
              <div key={f.key}>
                <label className="text-[10px] text-[var(--text-muted)] font-semibold block mb-0.5">
                  {f.label}{f.required && <span className="text-red-400 ml-0.5">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={f.key === 'apiKey' && !showKey ? 'password' : 'text'}
                    placeholder={f.ph}
                    value={creds[f.key]}
                    onChange={e => onChange({ ...creds, [f.key]: e.target.value })}
                    className={inputCls}
                  />
                  {f.key === 'apiKey' && (
                    <button onClick={() => setShowKey(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                      {showKey ? <EyeOff size={11} /> : <Eye size={11} />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Save as new */}
          <div className="border-t border-[var(--border-color)] pt-2.5">
            <p className="text-[10px] text-[var(--text-muted)] font-semibold mb-1.5">Save as new configuration</p>
            <div className="flex gap-2">
              <input
                className={inputCls + ' flex-1'}
                placeholder="e.g. Acme UAT, Client demo…"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveNew() }}
              />
              <Button variant="secondary" size="sm" onClick={saveNew} disabled={saving || !saveName.trim() || !creds.customerId || !creds.apiKey}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>

        </div>
      )}
    </div>
  )
}

// ── How it works ──────────────────────────────────────────────────────────────

const HOW_IT_WORKS: Record<string, { title: string; steps: { n: string; step: string; method: string; endpoint: string; note: string }[] }> = {
  kyc: { title: 'KYC via API — sequence', steps: [
    { n: '1', step: 'Test Connection', method: 'GET', endpoint: '/kyc/ruok', note: 'Verify credentials and connectivity' },
    { n: '2', step: 'Load Workflows', method: 'GET', endpoint: '/workflows', note: 'Discover available KYC service profiles' },
    { n: '3a', step: 'Create Individual', method: 'POST', endpoint: '/individuals', note: 'Create entity without running workflow' },
    { n: '3b', step: 'Create + Execute', method: 'POST', endpoint: '/individuals/new/serviceprofiles/DEFAULT/workflows/{name}/execute', note: 'Create + run in one call — skip step 4' },
    { n: '4', step: 'Execute Workflow', method: 'POST', endpoint: '/individuals/{entityId}/serviceprofiles/DEFAULT/workflows/{name}/execute', note: 'Run workflow on existing entity' },
    { n: '5', step: 'Poll Result', method: 'GET', endpoint: '/individuals/{entityId}/serviceprofiles/DEFAULT/workflows/{name}/executions/{wfeId}', note: 'Repeat until COMPLETED | ERROR | TIMEOUT' },
  ]},
  kyb: { title: 'KYB via API — sequence', steps: [
    { n: '1', step: 'Test Connection', method: 'GET', endpoint: '/kyc/ruok', note: 'Verify credentials' },
    { n: '2', step: 'Load Workflows', method: 'GET', endpoint: '/workflows', note: 'Discover KYB workflows' },
    { n: '3', step: 'Business Lookup', method: 'POST', endpoint: '/organizations/lookup', note: 'Search ABR/ASIC — returns organizationToken' },
    { n: '4a', step: 'Create Org', method: 'POST', endpoint: '/organizations', note: 'Create org entity from token — returns entityId' },
    { n: '4b', step: 'Execute for Entity', method: 'POST', endpoint: '/organizations/{id}/serviceprofiles/{svc}/workflows/{wf}/execute', note: 'Returns 202 Accepted — async' },
    { n: '4c', step: 'Execute by Token', method: 'POST', endpoint: '/organizations/workflows/{wf}/execute', note: 'Skips create — 202 Accepted' },
    { n: '5', step: 'Poll Result', method: 'GET', endpoint: '/organizations/{id}/serviceprofiles/{svc}/workflows/{wf}/executions/{wfeId}', note: 'Poll until COMPLETED' },
  ]},
  hosted: { title: 'Hosted OneSDK — sequence', steps: [
    { n: '1', step: 'Generate URL', method: 'POST', endpoint: '/individuals/hostedUrl', note: 'Returns { entityId, url } — load URL in iframe' },
    { n: '2', step: 'User completes', method: '—', endpoint: 'hosted.uat.frankie.one', note: 'FrankieOne hosted UI handles capture + biometrics' },
    { n: '3', step: 'postMessage event', method: '—', endpoint: 'X-Frankie-Source: hosted-onesdk', note: 'type: SUCCESS | FAILURE | PENDING' },
    { n: '4', step: 'Get Result', method: 'GET', endpoint: '/individuals/{entityId}', note: 'Fetch final result' },
  ]},
  embedded: { title: 'Embedded OneSDK — sequence', steps: [
    { n: '1', step: 'Get Session Token', method: 'POST', endpoint: '/auth/v2/machine-session', note: 'Server mints one-time token — returns token + entityId' },
    { n: '2', step: 'Init SDK', method: '—', endpoint: 'OneSdk({ session: { token } })', note: 'SDK renders capture UI in #onesdk-container' },
    { n: '3', step: 'User completes', method: '—', endpoint: 'instance.on("results")', note: 'SDK fires completion event' },
    { n: '4', step: 'Get Result', method: 'GET', endpoint: '/individuals/{entityId}', note: 'Fetch final result from API' },
  ]},
}

function HowItWorksPanel({ tabId }: { tabId: string }) {
  const content = HOW_IT_WORKS[tabId]
  if (!content) return null
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold text-[var(--text-secondary)]">{content.title}</p>
      <div className="border border-[var(--border-color)] rounded overflow-hidden">
        <div className="grid grid-cols-[1.5rem_1fr_5rem_1fr] gap-x-3 px-3 py-1.5 bg-[var(--bg-primary)] border-b border-[var(--border-color)]">
          {['#', 'Step', 'Method', 'Endpoint / Note'].map(h => <span key={h} className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">{h}</span>)}
        </div>
        {content.steps.map((s, i) => (
          <div key={i} className={`grid grid-cols-[1.5rem_1fr_5rem_1fr] gap-x-3 px-3 py-2 border-b border-[var(--border-color)] last:border-0 ${i % 2 === 0 ? 'bg-[var(--bg-secondary)]' : 'bg-[var(--bg-secondary)]/60'}`}>
            <span className="text-[10px] font-mono text-[var(--text-muted)]">{s.n}</span>
            <span className="text-[10px] text-[var(--text-secondary)] font-medium">{s.step}</span>
            <span className={`text-[10px] font-mono font-bold ${s.method === 'GET' ? 'text-sky-400' : s.method === 'POST' ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>{s.method}</span>
            <div>
              <code className="text-[9px] font-mono text-violet-300 break-all">{s.endpoint}</code>
              <p className="text-[9px] text-[var(--text-muted)] mt-0.5">{s.note}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Curl preview ──────────────────────────────────────────────────────────────

function CurlPreview({ method, url, creds, body }: { method: string; url: string; creds: Creds; body?: unknown }) {
  const [copied, setCopied] = useState(false)
  const parts: string[] = [`curl --location --request ${method}`]
  parts.push(`  '${url}'`)
  if (body !== undefined) parts.push(`  --header 'Content-Type: application/json'`)
  parts.push(`  --header 'X-Frankie-CustomerID: ${creds.customerId || '{customerId}'}'`)
  parts.push(`  --header 'api_key: ${creds.apiKey || '{apiKey}'}'`)
  if (creds.childCustomerId) parts.push(`  --header 'X-Frankie-CustomerChildID: ${creds.childCustomerId}'`)
  if (body !== undefined) parts.push(`  --data-raw '${JSON.stringify(body, null, 2)}'`)
  const curlText = parts.join(' \\\n')
  function copy() { navigator.clipboard.writeText(curlText); setCopied(true); setTimeout(() => setCopied(false), 1500) }
  return (
    <div className="relative">
      <button onClick={copy} className="absolute top-1 right-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors z-10">
        {copied ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
      </button>
      <pre className="text-[10px] font-mono text-[var(--text-secondary)] whitespace-pre-wrap break-all leading-relaxed pr-5">{curlText}</pre>
    </div>
  )
}

function CurlToggle({ method, url, creds, body }: { method: string; url: string; creds: Creds; body?: unknown }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-1.5">
      <button className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-medium bg-violet-500/15 text-violet-400 border border-violet-500/30 hover:bg-violet-500/25 transition-colors" onClick={() => setOpen(o => !o)}>
        <ChevronDown size={9} className={open ? '' : '-rotate-90'} />
        {open ? 'hide curl' : 'curl'}
      </button>
      {open && (
        <div className="mt-1 bg-[var(--bg-primary)] rounded border border-[var(--border-color)] p-2 max-h-48 overflow-y-auto">
          <CurlPreview method={method} url={url} creds={creds} body={body} />
        </div>
      )}
    </div>
  )
}

// ── API call helpers ──────────────────────────────────────────────────────────

function proxyUrl(frankiePath: string): string {
  const v2Path = frankiePath.startsWith('/') ? frankiePath : `/${frankiePath}`
  return `/api/frankie-proxy${v2Path}`
}

function buildHeaders(creds: Creds, withBody = false): Record<string, string> {
  const h: Record<string, string> = {
    'X-FT-Customer-ID': creds.customerId,
    'X-FT-API-Key': creds.apiKey,
    'X-FT-API-Base': creds.apiBase || DEFAULT_CREDS.apiBase,
    'Accept': 'application/json',
  }
  if (creds.childCustomerId) h['X-FT-Child-Customer-ID'] = creds.childCustomerId
  if (withBody) h['Content-Type'] = 'application/json'
  return h
}

function buildMachineHeaders(creds: Creds, withBody = false): Record<string, string> {
  const h: Record<string, string> = {
    'X-FT-Customer-ID': creds.customerId,
    'X-FT-API-Key': creds.apiKey,
    'X-FT-Machine-Base': creds.machineBase || DEFAULT_CREDS.machineBase,
    'Accept': 'application/json',
  }
  if (withBody) h['Content-Type'] = 'application/json'
  return h
}

function tabPanelBtn(active: boolean): string {
  return `flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium border transition-colors ${active ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] border-[var(--border-color)]' : 'text-[var(--text-muted)] border-transparent hover:text-[var(--text-primary)]'}`
}

// ── KYC Tab ───────────────────────────────────────────────────────────────────

function scenarioToForm(s: KycScenario): IndividualForm {
  const ind = s.rawPayload.individual
  const name = (ind.name ?? {}) as Record<string, string>
  const dob = (ind.dateOfBirth ?? {}) as Record<string, string>
  const addr = ((ind.addresses ?? []) as Record<string, string>[])[0] ?? {}
  const doc = ((ind.documents as { IDENTITY?: Record<string, string>[] } | undefined)?.IDENTITY ?? [])[0] ?? {}
  return {
    givenName: name.givenName ?? '', middleName: name.middleName ?? '', familyName: name.familyName ?? '',
    dobYear: dob.year ?? '', dobMonth: dob.month ?? '', dobDay: dob.day ?? '',
    nationality: (ind.nationality as string) ?? 'AUS',
    addressStreetNumber: addr.streetNumber ?? '', addressStreetName: addr.streetName ?? '', addressStreetType: addr.streetType ?? '',
    addressLocality: addr.locality ?? '', addressSubdivision: addr.subdivision ?? '', addressPostalCode: addr.postalCode ?? '', addressCountry: addr.country ?? 'AUS',
    docType: doc.type ?? 'PASSPORT', docNumber: doc.primaryIdentifier ?? '', docSecondaryIdentifier: doc.secondaryIdentifier ?? '',
    docCountry: doc.country ?? 'AUS', docSubdivision: doc.subdivision ?? '',
    consentGeneral: true, consentDocs: true, externalRef: '',
  }
}

export function KycTab({ creds, addLog }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const [panel, setPanel] = useState<'how' | null>(null)
  const [connStatus, setConnStatus] = useState<'idle' | 'ok' | 'error'>('idle')
  const [workflows, setWorkflows] = useState<{ serviceName: string; workflowName: string }[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState('')
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [form, setForm] = useState<IndividualForm>(BLANK_FORM)
  // Raw payload override — used when a scenario has fields that buildIndividual() can't represent
  // (e.g. supplementaryData, subtype, expiryDate on documents). Cleared when form is manually edited.
  const [rawPayloadOverride, setRawPayloadOverride] = useState<{ individual: Record<string, unknown> } | null>(null)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  const [pollingMsg, setPollingMsg] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [workflowResult, setWorkflowResult] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)

  const individualPayload = rawPayloadOverride ?? buildIndividual(form)
  const createAndExecPayload = { workflowName: selectedWorkflow, ...individualPayload }

  function updateForm(updater: (f: IndividualForm) => IndividualForm) {
    setRawPayloadOverride(null)
    setForm(updater)
  }

  async function doFetch(method: string, path: string, body: unknown | undefined, annotation: string) {
    const url = `${apiBase}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(creds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }

  async function testConnection() {
    setLoading('conn')
    try { await doFetch('GET', '/kyc/ruok', undefined, 'Health check — verifies API credentials'); setConnStatus('ok') }
    catch { setConnStatus('error') } finally { setLoading(null) }
  }

  async function loadWorkflows() {
    setLoading('wf')
    try {
      const data = await doFetch('GET', '/workflows', undefined, 'List configured KYC workflows') as { workflows?: { serviceName: string; workflowName: string }[] }
      const wfs = data?.workflows ?? []
      setWorkflows(wfs)
      if (wfs.length > 0) setSelectedWorkflow(wfs[0].workflowName)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function createIndividual() {
    setLoading('create')
    try {
      const data = await doFetch('POST', '/individuals', individualPayload, 'Create individual entity (no workflow)') as { individual?: { entityId?: string } }
      if (data?.individual?.entityId) setEntityId(data.individual.entityId)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function createAndExecute() {
    if (!selectedWorkflow) return
    setLoading('exec')
    setWorkflowResult(null); setFullResult(null)
    try {
      const data = await doFetch('POST', `/individuals/new/serviceprofiles/DEFAULT/workflows/${selectedWorkflow}/execute`, createAndExecPayload, 'Create individual + execute KYC in one call') as { individual?: { entityId?: string }; workflowResult?: { workflowExecutionState?: string; workflowExecutionId?: string } }
      if (data?.individual?.entityId) setEntityId(data.individual.entityId)
      const wr = data?.workflowResult
      const state = wr?.workflowExecutionState
      if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') { setWorkflowResult(wr); setFullResult(data) }
      else if (wr?.workflowExecutionId && data?.individual?.entityId) { await pollResult(data.individual.entityId, wr.workflowExecutionId) }
      else { setWorkflowResult(wr ?? null); setFullResult(data) }
    } catch { /* logged */ } finally { setLoading(null); setPollingMsg('') }
  }

  async function executeWorkflow() {
    if (!entityId || !selectedWorkflow) return
    setLoading('execwf')
    setWorkflowResult(null); setFullResult(null)
    try {
      const data = await doFetch('POST', `/individuals/${entityId}/serviceprofiles/DEFAULT/workflows/${selectedWorkflow}/execute`, { workflowName: selectedWorkflow }, 'Execute KYC workflow on existing entity') as { workflowResult?: { workflowExecutionState?: string; workflowExecutionId?: string } }
      const wr = data?.workflowResult
      const state = wr?.workflowExecutionState
      if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') { setWorkflowResult(wr); setFullResult(data) }
      else if (wr?.workflowExecutionId) { await pollResult(entityId, wr.workflowExecutionId) }
    } catch { /* logged */ } finally { setLoading(null); setPollingMsg('') }
  }

  async function pollResult(eid: string, wfeId: string) {
    for (let i = 0; i < 20; i++) {
      setPollingMsg(`Polling… attempt ${i + 1}/20`)
      try {
        const data = await doFetch('GET', `/individuals/${eid}/serviceprofiles/DEFAULT/workflows/${selectedWorkflow}/executions/${wfeId}`, undefined, `Poll KYC result (attempt ${i + 1}/20)`) as { workflowResult?: { workflowExecutionState?: string } }
        const state = data?.workflowResult?.workflowExecutionState
        if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') { setWorkflowResult(data.workflowResult); setFullResult(data); return }
      } catch { /* keep polling */ }
      await new Promise(r => setTimeout(r, 4000))
    }
    setPollingMsg('Polling timed out')
  }

  function reset() { setEntityId(null); setWorkflowResult(null); setFullResult(null); setConnStatus('idle'); setPollingMsg(''); setWorkflows([]); setSelectedWorkflow('') }

  const inputCls = 'w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs font-mono text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-primary'

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 justify-end">
        <button onClick={() => setPanel(p => p === 'how' ? null : 'how')} className={tabPanelBtn(panel === 'how')}>
          <Info size={11} /> How it works
        </button>
      </div>
      {panel === 'how' && <Card className="p-4"><HowItWorksPanel tabId="kyc" /></Card>}

      {/* Step 1 */}
      <Card className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 1 — Test Connection</p>
          {connStatus !== 'idle' && <span className={`flex items-center gap-1 text-[10px] ${connStatus === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>{connStatus === 'ok' ? <Wifi size={11} /> : <WifiOff size={11} />}{connStatus === 'ok' ? 'Connected' : 'Failed'}</span>}
        </div>
        <p className="text-[10px] text-[var(--text-muted)]">Verify credentials at <code className="font-mono text-amber-400">{apiBase}/kyc/ruok</code></p>
        <Button variant="secondary" size="sm" onClick={testConnection} disabled={loading !== null || !hasCreds}>
          {loading === 'conn' ? <RefreshCw size={11} className="animate-spin mr-1" /> : <Wifi size={11} className="mr-1" />}
          {loading === 'conn' ? 'Checking…' : 'Test Connection'}
        </Button>
        <CurlToggle method="GET" url={`${apiBase}/kyc/ruok`} creds={creds} />
        {!hasCreds && <p className="text-[10px] text-amber-400">Add credentials above to make live calls.</p>}
      </Card>

      {/* Step 2 */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 2 — Load Workflows</p>
        <p className="text-[10px] text-[var(--text-muted)]">Fetch available KYC service profiles from <code className="font-mono text-amber-400">/workflows</code></p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" onClick={loadWorkflows} disabled={loading !== null || !hasCreds}>
            {loading === 'wf' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
            {loading === 'wf' ? 'Loading…' : 'Load Workflows'}
          </Button>
          {workflows.length > 0 && (
            <select value={selectedWorkflow} onChange={e => setSelectedWorkflow(e.target.value)} className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
              {workflows.map(w => <option key={w.workflowName} value={w.workflowName}>{w.serviceName} / {w.workflowName}</option>)}
            </select>
          )}
          {workflows.length > 0 && <span className="text-[10px] text-emerald-400">{workflows.length} workflow{workflows.length !== 1 ? 's' : ''} loaded</span>}
        </div>
        <CurlToggle method="GET" url={`${apiBase}/workflows`} creds={creds} />
      </Card>

      {/* Step 3 */}
      <Card className="p-3 space-y-3">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 3 — Individual Data</p>

        {/* Scenario selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={scenarioIdx ?? ''}
            onChange={e => {
            const v = e.target.value
            if (v === '') { setScenarioIdx(null); setForm(BLANK_FORM); setRawPayloadOverride(null) }
            else { const idx = parseInt(v); setScenarioIdx(idx); setForm(scenarioToForm(KYC_SCENARIOS[idx])); setRawPayloadOverride(KYC_SCENARIOS[idx].rawPayload) }
          }}
            className="flex-1 min-w-0 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">— Custom / manual entry —</option>
            {(['Australian', 'International'] as const).map(cat => (
              <optgroup key={cat} label={cat}>
                {KYC_SCENARIOS.map((s, i) => s.category === cat ? <option key={i} value={i}>{s.label}</option> : null)}
              </optgroup>
            ))}
          </select>
          {scenarioIdx !== null && <span className={`text-[10px] font-bold shrink-0 ${resultColor(KYC_SCENARIOS[scenarioIdx].expectedResult)}`}>Expected: {KYC_SCENARIOS[scenarioIdx].expectedResult}</span>}
        </div>

        {/* Always-visible form — autofilled from scenario or blank */}
        <div className="space-y-3">
          <div className="space-y-2">
            <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Personal Details</p>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">Given Name</label><input className={inputCls} value={form.givenName} onChange={e => updateForm(f => ({ ...f, givenName: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Middle Name</label><input className={inputCls} value={form.middleName} onChange={e => updateForm(f => ({ ...f, middleName: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Family Name</label><input className={inputCls} value={form.familyName} onChange={e => updateForm(f => ({ ...f, familyName: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">DOB Year</label><input className={inputCls} placeholder="YYYY" value={form.dobYear} onChange={e => updateForm(f => ({ ...f, dobYear: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Month</label><input className={inputCls} placeholder="MM" value={form.dobMonth} onChange={e => updateForm(f => ({ ...f, dobMonth: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Day</label><input className={inputCls} placeholder="DD" value={form.dobDay} onChange={e => updateForm(f => ({ ...f, dobDay: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Nationality</label><input className={inputCls} value={form.nationality} onChange={e => updateForm(f => ({ ...f, nationality: e.target.value }))} /></div>
            </div>
          </div>
          <div className="border-t border-[var(--border-color)] pt-2 space-y-2">
            <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Address</p>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">Street No.</label><input className={inputCls} value={form.addressStreetNumber} onChange={e => updateForm(f => ({ ...f, addressStreetNumber: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Street Name</label><input className={inputCls} value={form.addressStreetName} onChange={e => updateForm(f => ({ ...f, addressStreetName: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Street Type</label><input className={inputCls} placeholder="STREET" value={form.addressStreetType} onChange={e => updateForm(f => ({ ...f, addressStreetType: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">Suburb</label><input className={inputCls} value={form.addressLocality} onChange={e => updateForm(f => ({ ...f, addressLocality: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">State</label><input className={inputCls} value={form.addressSubdivision} onChange={e => updateForm(f => ({ ...f, addressSubdivision: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Postcode</label><input className={inputCls} value={form.addressPostalCode} onChange={e => updateForm(f => ({ ...f, addressPostalCode: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Country</label><input className={inputCls} value={form.addressCountry} onChange={e => updateForm(f => ({ ...f, addressCountry: e.target.value }))} /></div>
            </div>
          </div>
          <div className="border-t border-[var(--border-color)] pt-2 space-y-2">
            <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Identity Document</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-[var(--text-muted)]">Document Type</label>
                <select className={inputCls} value={form.docType} onChange={e => updateForm(f => ({ ...f, docType: e.target.value }))}>
                  {['PASSPORT','DRIVERS_LICENSE','NATIONAL_ID','NATIONAL_HEALTH_ID','MEDICARE_CARD','BIRTH_CERT'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Doc Number</label><input className={inputCls} value={form.docNumber} onChange={e => updateForm(f => ({ ...f, docNumber: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Country</label><input className={inputCls} value={form.docCountry} onChange={e => updateForm(f => ({ ...f, docCountry: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">Secondary ID (card/ref no.)</label><input className={inputCls} value={form.docSecondaryIdentifier} onChange={e => updateForm(f => ({ ...f, docSecondaryIdentifier: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">State/Subdivision (license)</label><input className={inputCls} value={form.docSubdivision} onChange={e => updateForm(f => ({ ...f, docSubdivision: e.target.value }))} /></div>
            </div>
          </div>
          <div className="border-t border-[var(--border-color)] pt-2 flex gap-4">
            <label className="flex items-center gap-1 text-[10px] text-[var(--text-secondary)] cursor-pointer"><input type="checkbox" checked={form.consentGeneral} onChange={e => updateForm(f => ({ ...f, consentGeneral: e.target.checked }))} className="rounded" /> GENERAL consent</label>
            <label className="flex items-center gap-1 text-[10px] text-[var(--text-secondary)] cursor-pointer"><input type="checkbox" checked={form.consentDocs} onChange={e => updateForm(f => ({ ...f, consentDocs: e.target.checked }))} className="rounded" /> DOCS consent</label>
          </div>
        </div>

        {/* Action buttons */}
        <div className="space-y-3 pt-1">
          <div>
            <p className="text-[9px] text-[var(--text-muted)] mb-1 font-mono">POST {apiBase}/individuals</p>
            <Button variant="secondary" size="sm" onClick={createIndividual} disabled={loading !== null || !hasCreds}>
              {loading === 'create' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
              {loading === 'create' ? 'Creating…' : 'Create Individual'}
            </Button>
            <CurlToggle method="POST" url={`${apiBase}/individuals`} creds={creds} body={individualPayload} />
          </div>
          <div className="flex items-center justify-center"><span className="text-[10px] text-[var(--text-muted)] font-medium">or</span></div>
          <div>
            <p className="text-[9px] text-[var(--text-muted)] mb-1 font-mono">POST {apiBase}/individuals/new/serviceprofiles/DEFAULT/workflows/{selectedWorkflow || '{workflow}'}/execute</p>
            <Button variant="primary" size="sm" onClick={createAndExecute} disabled={loading !== null || !hasCreds || !selectedWorkflow}>
              {loading === 'exec' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
              {loading === 'exec' ? (pollingMsg || 'Running…') : 'Create + Execute'}
            </Button>
            <CurlToggle method="POST" url={`${apiBase}/individuals/new/serviceprofiles/DEFAULT/workflows/${selectedWorkflow || '{workflow}'}/execute`} creds={creds} body={createAndExecPayload} />
            {!selectedWorkflow && hasCreds && <p className="text-[10px] text-amber-400 mt-1">Load workflows (Step 2) to enable.</p>}
          </div>
          {entityId && (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[10px]">
              <span className="text-emerald-400 font-semibold">Created</span>
              <span className="text-[var(--text-muted)]">entityId:</span>
              <code className="font-mono text-emerald-400 break-all">{entityId}</code>
              <button onClick={reset} className="ml-auto text-[var(--text-muted)] hover:text-red-400"><X size={10} /></button>
            </div>
          )}
        </div>
      </Card>

      {/* Step 4 */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 4 — Execute Workflow</p>
        {entityId
          ? <p className="text-[10px] text-[var(--text-muted)] font-mono">entityId: {entityId}</p>
          : <p className="text-[10px] text-[var(--text-muted)] italic">Create an individual (Step 3) to get an entityId</p>}
        <p className="text-[9px] text-[var(--text-muted)] font-mono">POST {apiBase}/individuals/{entityId || '{entityId}'}/serviceprofiles/DEFAULT/workflows/{selectedWorkflow || '{workflow}'}/execute</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" onClick={executeWorkflow} disabled={loading !== null || !hasCreds || !entityId || !selectedWorkflow}>
            {loading === 'execwf' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
            {loading === 'execwf' ? (pollingMsg || 'Running…') : 'Execute Workflow'}
          </Button>
          {pollingMsg && loading === 'execwf' && <p className="text-[10px] text-blue-400 font-mono">⟳ {pollingMsg}</p>}
        </div>
        <CurlToggle method="POST" url={`${apiBase}/individuals/${entityId || '{entityId}'}/serviceprofiles/DEFAULT/workflows/${selectedWorkflow || '{workflow}'}/execute`} creds={creds} body={{ workflowName: selectedWorkflow || '{workflow}' }} />
      </Card>

      {/* Step 5 */}
      <Card className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 5 — Results</p>
          {(workflowResult || entityId) && <button onClick={reset} className="text-[10px] text-[var(--text-muted)] hover:text-red-400">Reset</button>}
        </div>
        {workflowResult ? (
          <KycResultSummary wr={workflowResult} fullResponse={fullResult} expectedResult={scenarioIdx !== null ? KYC_SCENARIOS[scenarioIdx].expectedResult : undefined} />
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] italic">No results yet — run a workflow above</p>
        )}
      </Card>

      <InlineWebhookPanel entityId={entityId} customerId={creds.customerId ?? ''} />
    </div>
  )
}

// ── KYB Entity Result Panel ───────────────────────────────────────────────────

function EntityResultPanel({ entityResult }: { entityResult: unknown }) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = entityResult as any
    const org = raw?.organization ?? raw
    // UBOs/officials/shareholders reference individuals by entityId — resolve names from linkedIndividuals
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const linked: Record<string, any> = org?.linkedIndividuals ?? {}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function resolvedName(entityId: string): string {
      const ind = linked[entityId]
      const fullName = `${ind?.name?.givenName ?? ''} ${ind?.name?.familyName ?? ''}`.trim()
      return ind?.name?.displayName ?? (fullName || (entityId.slice(0, 8) + '…'))
    }
    const ubos: unknown[] = Array.isArray(org?.ultimateBeneficialOwners) ? org.ultimateBeneficialOwners : []
    const officials: unknown[] = Array.isArray(org?.officials) ? org.officials : []
    const shareholders: unknown[] = Array.isArray(org?.shareholders) ? org.shareholders : []
    const noOwnership = ubos.length === 0 && officials.length === 0 && shareholders.length === 0
    const legalName = org?.details?.name?.name ?? org?.details?.name ?? org?.name?.name
    const statusCode = org?.details?.status?.normalized?.code
    return (
      <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-2 mt-1">
        <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">GET /organizations/{'{entityId}'} — enriched entity profile</p>
        <div className="space-y-2 text-[10px]">
          {org?.entityId && <div className="flex gap-2"><span className="text-[var(--text-muted)] font-mono w-28 shrink-0">entityId:</span><span className="text-[var(--text-secondary)] font-mono">{String(org.entityId)}</span></div>}
          {legalName && <div className="flex gap-2"><span className="text-[var(--text-muted)] font-mono w-28 shrink-0">Legal Name:</span><span className="text-[var(--text-secondary)] font-mono">{String(legalName)}</span></div>}
          {statusCode && <div className="flex gap-2"><span className="text-[var(--text-muted)] font-mono w-28 shrink-0">Status:</span><span className={`font-mono font-semibold ${statusCode === 'ACTV' ? 'text-emerald-400' : 'text-amber-400'}`}>{statusCode}</span></div>}
          {ubos.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-emerald-400 mb-1">UBOs ({ubos.length})</p>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {ubos.map((u: any, i: number) => (
                <div key={i} className="flex items-center gap-2 py-0.5 border-t border-[var(--border-color)] text-[var(--text-secondary)] font-mono">
                  <span className="flex-1">{resolvedName(u?.entityId ?? '')}</span>
                  {u?.percentageOwned?.total != null && <span className="text-emerald-400">{u.percentageOwned.total}%</span>}
                </div>
              ))}
            </div>
          )}
          {officials.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-sky-400 mb-1">Officials / Directors ({officials.length})</p>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {officials.map((o: any, i: number) => (
                <div key={i} className="flex items-center gap-2 py-0.5 border-t border-[var(--border-color)] text-[var(--text-secondary)] font-mono">
                  <span className="flex-1">{resolvedName(o?.entityId ?? '')}</span>
                  {o?.role?.description && <span className="text-sky-400 text-[9px]">{String(o.role.description)}</span>}
                </div>
              ))}
            </div>
          )}
          {shareholders.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-violet-400 mb-1">Shareholders ({shareholders.length})</p>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {shareholders.map((s: any, i: number) => (
                <div key={i} className="flex items-center gap-2 py-0.5 border-t border-[var(--border-color)] text-[var(--text-secondary)] font-mono">
                  <span className="flex-1">{resolvedName(s?.entityId ?? '')}</span>
                  {s?.totalShares != null && <span className="text-violet-400">{s.totalShares} shares</span>}
                </div>
              ))}
            </div>
          )}
          {noOwnership && <p className="text-[var(--text-muted)] italic">No ownership data yet — run an ownership workflow first</p>}
          <details className="mt-1">
            <summary className="text-[9px] text-[var(--text-muted)] cursor-pointer hover:text-[var(--text-secondary)]">Raw JSON</summary>
            <pre className="mt-1 text-[9px] text-[var(--text-muted)] font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto">{JSON.stringify(entityResult, null, 2)}</pre>
          </details>
        </div>
      </div>
    )
  } catch {
    return (
      <div className="border border-[var(--border-color)] rounded-lg p-3 mt-1">
        <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-2">GET /organizations/{'{entityId}'} — raw response</p>
        <pre className="text-[9px] text-[var(--text-muted)] font-mono whitespace-pre-wrap break-all max-h-48 overflow-y-auto">{JSON.stringify(entityResult, null, 2)}</pre>
      </div>
    )
  }
}

// ── KYB Tab ───────────────────────────────────────────────────────────────────

export function KybTab({ creds, addLog }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const [panel, setPanel] = useState<'how' | null>(null)
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [workflows, setWorkflows] = useState<{ serviceName: string; workflowName: string }[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState('')
  const [kybServiceName, setKybServiceName] = useState('KYB')
  const [searchBy, setSearchBy] = useState<'name' | 'registration'>('registration')
  const [searchName, setSearchName] = useState('')
  const [searchRegNum, setSearchRegNum] = useState('')
  const [searchRegType, setSearchRegType] = useState('ACN')
  const [searchCountry, setSearchCountry] = useState('AUS')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [lookupResults, setLookupResults] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedOrg, setSelectedOrg] = useState<any | null>(null)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [workflowExecutionId, setWorkflowExecutionId] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  const [pollingMsg, setPollingMsg] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [workflowResult, setWorkflowResult] = useState<any | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [resultOrg, setResultOrg] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const [entityResult, setEntityResult] = useState<unknown>(null)

  async function doFetch(method: string, path: string, body: unknown | undefined, annotation: string) {
    const url = `${apiBase}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(creds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }

  async function loadWorkflows() {
    setLoading('wf')
    try {
      const data = await doFetch('GET', '/workflows', undefined, 'List configured KYB workflows') as { workflows?: { serviceName: string; workflowName: string }[] }
      const all = data?.workflows ?? []
      const kyb = all.filter(w => w.serviceName?.toUpperCase().includes('KYB'))
      const toShow = kyb.length > 0 ? kyb : all
      setWorkflows(toShow)
      if (toShow.length > 0) { setSelectedWorkflow(toShow[0].workflowName); setKybServiceName(toShow[0].serviceName ?? 'KYB') }
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function lookupBusiness() {
    setLoading('lookup')
    const body = searchBy === 'name'
      ? { organizationName: searchName, region: { country: searchCountry } }
      : { organizationNumber: { registrationNumber: searchRegNum, registryCode: searchRegType }, region: { country: searchCountry } }
    try {
      const data = await doFetch('POST', '/organizations/lookup', body, 'Search business registry (ABR/ASIC)') as { matchedOrganizations?: unknown[] }
      setLookupResults(data?.matchedOrganizations ?? [])
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function createOrganization() {
    if (!selectedOrg) return
    setLoading('createOrg')
    try {
      const data = await doFetch('POST', '/organizations', { organizationToken: selectedOrg.organizationToken }, 'Create organisation entity from lookup token') as { organization?: { entityId?: string }; entityId?: string }
      const eid = data?.organization?.entityId ?? data?.entityId
      if (eid) setEntityId(eid)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function executeForEntity() {
    if (!entityId || !selectedWorkflow) return
    setLoading('execEntity')
    try {
      const data = await doFetch('POST', `/organizations/${entityId}/serviceprofiles/${kybServiceName}/workflows/${selectedWorkflow}/execute`, { serviceName: kybServiceName, workflowName: selectedWorkflow }, 'Execute KYB workflow for organisation entity — returns 202 async') as { workflowExecutionId?: string }
      if (data?.workflowExecutionId) setWorkflowExecutionId(data.workflowExecutionId)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function executeByToken() {
    if (!selectedOrg || !selectedWorkflow) return
    setLoading('execToken')
    try {
      const data = await doFetch('POST', `/organizations/workflows/${selectedWorkflow}/execute`, { organizationToken: selectedOrg.organizationToken }, 'Execute KYB workflow by token — skips create step — returns 202 async') as { entityId?: string; workflowExecutionId?: string }
      if (data?.entityId) setEntityId(data.entityId)
      if (data?.workflowExecutionId) setWorkflowExecutionId(data.workflowExecutionId)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function getResult() {
    if (!entityId || !workflowExecutionId) return
    setLoading('result')
    try {
      const data = await doFetch('GET', `/organizations/${entityId}/serviceprofiles/${kybServiceName}/workflows/${selectedWorkflow}/executions/${workflowExecutionId}`, undefined, 'Get KYB workflow execution result') as { workflowResult?: unknown; organization?: unknown }
      if (data?.workflowResult) { setWorkflowResult(data.workflowResult); setResultOrg(data.organization ?? null); setFullResult(data) }
      else setPollingMsg('No result yet — try again')
    } catch { /* logged */ } finally { setLoading(null) }
  }

  function applyScenario(idx: number) {
    const s = KYB_SCENARIOS[idx]
    setSearchBy('registration')
    setSearchRegType(s.searchRegType)
    setSearchRegNum(s.searchValue)
    setSearchName(s.name)
    setLookupResults([]); setSelectedOrg(null)
  }

  async function getEntity() {
    if (!entityId) return
    setLoading('entity')
    try {
      const data = await doFetch('GET', `/organizations/${entityId}`, undefined, 'Get organisation entity — enriched profile including ownership data')
      setEntityResult(data)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  function reset() { setEntityId(null); setWorkflowExecutionId(null); setWorkflowResult(null); setResultOrg(null); setFullResult(null); setEntityResult(null); setPollingMsg(''); setLookupResults([]); setSelectedOrg(null) }

  const inputCls = 'w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs font-mono text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-primary'

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 justify-end">
        <button onClick={() => setPanel(p => p === 'how' ? null : 'how')} className={tabPanelBtn(panel === 'how')}>
          <Info size={11} /> How it works
        </button>
      </div>
      {panel === 'how' && <Card className="p-4"><HowItWorksPanel tabId="kyb" /></Card>}

      {/* Step 1 — Connection */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 1 — Test Connection</p>
        <Button variant="secondary" size="sm" onClick={async () => { setLoading('conn'); try { await doFetch('GET', '/kyc/ruok', undefined, 'Health check'); } catch { /* logged */ } finally { setLoading(null) } }} disabled={loading !== null || !hasCreds}>
          {loading === 'conn' ? <RefreshCw size={11} className="animate-spin mr-1" /> : <Wifi size={11} className="mr-1" />}
          {loading === 'conn' ? 'Checking…' : 'Test Connection'}
        </Button>
        {!hasCreds && <p className="text-[10px] text-amber-400">Add credentials above to make live calls.</p>}
      </Card>

      {/* Step 2 — Workflows */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 2 — Load KYB Workflows</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" onClick={loadWorkflows} disabled={loading !== null || !hasCreds}>
            {loading === 'wf' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading === 'wf' ? 'Loading…' : 'Load Workflows'}
          </Button>
          {workflows.length > 0 && (
            <select value={selectedWorkflow} onChange={e => setSelectedWorkflow(e.target.value)} className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
              {workflows.map(w => <option key={w.workflowName} value={w.workflowName}>{w.workflowName}</option>)}
            </select>
          )}
        </div>
        <CurlToggle method="GET" url={`${apiBase}/workflows`} creds={creds} />
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div><label className="text-[10px] text-[var(--text-muted)]">Service Name</label><input className={inputCls} value={kybServiceName} onChange={e => setKybServiceName(e.target.value)} placeholder="KYB" /></div>
          <div><label className="text-[10px] text-[var(--text-muted)]">Workflow Name</label><input className={inputCls} value={selectedWorkflow} onChange={e => setSelectedWorkflow(e.target.value)} placeholder="AUS-Organization-Ownership" /></div>
        </div>
      </Card>

      {/* Step 3 — Lookup */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 3 — Business Lookup</p>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={scenarioIdx ?? ''} onChange={e => { const v = e.target.value; if (v === '') { setScenarioIdx(null) } else { const idx = parseInt(v); setScenarioIdx(idx); applyScenario(idx) } }}
            className="flex-1 min-w-0 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">— Manual entry —</option>
            {KYB_SCENARIOS.map((s, i) => <option key={i} value={i}>{s.label}</option>)}
          </select>
        </div>
        {scenarioIdx !== null && (
          <div className="flex items-center gap-2 flex-wrap text-[10px]">
            {KYB_SCENARIOS[scenarioIdx].acn && <span className="font-mono text-[var(--text-muted)]">ACN {KYB_SCENARIOS[scenarioIdx].acn}</span>}
            {KYB_SCENARIOS[scenarioIdx].abn && <span className="font-mono text-[var(--text-muted)]">· ABN {KYB_SCENARIOS[scenarioIdx].abn}</span>}
            {KYB_SCENARIOS[scenarioIdx].tags.map(t => <span key={t} className="bg-sky-500/10 text-sky-400 px-1.5 py-0.5 rounded text-[9px]">{t}</span>)}
          </div>
        )}
        <div className="flex gap-4 text-[10px]">
          <label className="flex items-center gap-1 cursor-pointer text-[var(--text-secondary)]"><input type="radio" checked={searchBy === 'name'} onChange={() => setSearchBy('name')} /> By Name</label>
          <label className="flex items-center gap-1 cursor-pointer text-[var(--text-secondary)]"><input type="radio" checked={searchBy === 'registration'} onChange={() => setSearchBy('registration')} /> By Registration No.</label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {searchBy === 'name' ? (
            <div className="col-span-2"><label className="text-[10px] text-[var(--text-muted)]">Organisation Name</label><input className={inputCls} value={searchName} onChange={e => setSearchName(e.target.value)} /></div>
          ) : (
            <>
              <div><label className="text-[10px] text-[var(--text-muted)]">Registration Number</label><input className={inputCls} value={searchRegNum} onChange={e => setSearchRegNum(e.target.value)} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Type</label><select className={inputCls} value={searchRegType} onChange={e => setSearchRegType(e.target.value)}><option>ABN</option><option>ACN</option><option>NZBN</option></select></div>
            </>
          )}
          <div><label className="text-[10px] text-[var(--text-muted)]">Country</label><input className={inputCls} value={searchCountry} onChange={e => setSearchCountry(e.target.value)} /></div>
        </div>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">POST {apiBase}/organizations/lookup</p>
        <Button variant="secondary" size="sm" onClick={lookupBusiness} disabled={loading !== null || !hasCreds}>
          {loading === 'lookup' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading === 'lookup' ? 'Searching…' : 'Search'}
        </Button>
        <CurlToggle method="POST" url={`${apiBase}/organizations/lookup`} creds={creds} body={searchBy === 'name' ? { organizationName: searchName, region: { country: searchCountry } } : { organizationNumber: { registrationNumber: searchRegNum, registryCode: searchRegType }, region: { country: searchCountry } }} />
        {lookupResults.length > 0 && (
          <div className="space-y-1.5 mt-1">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {lookupResults.map((org: any, i: number) => {
              const abn = org.registrationDetails?.find((r: { registrationNumberType: string }) => r.registrationNumberType === 'ABN')?.registrationNumber
              const isSelected = selectedOrg?.organizationToken === org.organizationToken
              return (
                <div key={i} className={`border rounded p-2.5 text-[10px] space-y-1 ${isSelected ? 'border-sky-500/40 bg-sky-500/10' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                  <p className="font-semibold text-[var(--text-primary)]">{org.name?.name ?? '—'}</p>
                  <div className="flex gap-3 text-[var(--text-muted)] flex-wrap">
                    {abn && <span>ABN: {abn}</span>}
                    {org.status?.normalized?.code && <span>Status: {org.status.normalized.code}</span>}
                    {org.type?.description && <span>{org.type.description}</span>}
                  </div>
                  <button className="text-[10px] text-sky-400 hover:text-sky-300 font-medium" onClick={() => setSelectedOrg(org)}>
                    {isSelected ? '✓ Selected' : 'Select'}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Step 4 — Create & Execute */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 4 — Create &amp; Execute</p>
        {selectedOrg ? (
          <p className="text-[10px] text-[var(--text-secondary)]">Selected: <span className="font-semibold">{selectedOrg.name?.name}</span></p>
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] italic">Select a business from lookup results above</p>
        )}
        <p className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1">KYB workflows always return <strong>202 Accepted</strong> — poll Step 5 for final result.</p>
        <div className="space-y-2">
          <div>
            <p className="text-[9px] text-[var(--text-muted)] mb-1 font-mono">POST {apiBase}/organizations</p>
            <Button variant="secondary" size="sm" onClick={createOrganization} disabled={loading !== null || !hasCreds || !selectedOrg}>
              {loading === 'createOrg' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading === 'createOrg' ? 'Creating…' : 'Create Organization'}
            </Button>
            <CurlToggle method="POST" url={`${apiBase}/organizations`} creds={creds} body={selectedOrg ? { organizationToken: selectedOrg.organizationToken } : { organizationToken: '{organizationToken}' }} />
          </div>
          {entityId && <p className="text-[10px] text-emerald-400 font-mono">entityId: {entityId}</p>}
          <div className="flex items-center justify-center"><span className="text-[10px] text-[var(--text-muted)]">then</span></div>
          <div>
            <p className="text-[9px] text-[var(--text-muted)] mb-1 font-mono">POST /organizations/{entityId || '{id}'}/serviceprofiles/{kybServiceName}/workflows/{selectedWorkflow || '{wf}'}/execute</p>
            <Button variant="primary" size="sm" onClick={executeForEntity} disabled={loading !== null || !hasCreds || !entityId || !selectedWorkflow}>
              {loading === 'execEntity' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading === 'execEntity' ? 'Executing…' : 'Execute for Entity'}
            </Button>
            <CurlToggle method="POST" url={`${apiBase}/organizations/${entityId || '{id}'}/serviceprofiles/${kybServiceName}/workflows/${selectedWorkflow || '{wf}'}/execute`} creds={creds} body={{ serviceName: kybServiceName, workflowName: selectedWorkflow || '{wf}' }} />
          </div>
          <div className="flex items-center justify-center"><span className="text-[10px] text-[var(--text-muted)]">or</span></div>
          <div>
            <p className="text-[9px] text-[var(--text-muted)] mb-1 font-mono">POST /organizations/workflows/{selectedWorkflow || '{wf}'}/execute</p>
            <Button variant="secondary" size="sm" onClick={executeByToken} disabled={loading !== null || !hasCreds || !selectedWorkflow || !selectedOrg}>
              {loading === 'execToken' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading === 'execToken' ? 'Executing…' : 'Execute by Token'}
            </Button>
            <CurlToggle method="POST" url={`${apiBase}/organizations/workflows/${selectedWorkflow || '{wf}'}/execute`} creds={creds} body={selectedOrg ? { organizationToken: selectedOrg.organizationToken } : { organizationToken: '{organizationToken}' }} />
          </div>
          {workflowExecutionId && (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-[10px]">
              <span className="text-blue-400 font-semibold">202 Accepted</span>
              <span className="text-[var(--text-muted)]">workflowExecutionId:</span>
              <code className="font-mono text-blue-400 break-all">{workflowExecutionId}</code>
            </div>
          )}
        </div>
      </Card>

      {/* Step 5 — Results */}
      <Card className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 5 — Results</p>
          {(workflowResult || entityId) && <button onClick={reset} className="text-[10px] text-[var(--text-muted)] hover:text-red-400">Reset</button>}
        </div>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">GET /organizations/{entityId || '{id}'}/serviceprofiles/{kybServiceName}/workflows/{selectedWorkflow || '{wf}'}/executions/{workflowExecutionId || '{wfeId}'}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" onClick={getResult} disabled={loading !== null || !hasCreds || !entityId || !workflowExecutionId}>
            {loading === 'result' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading === 'result' ? 'Checking…' : 'Get Workflow Result'}
          </Button>
          <Button variant="secondary" size="sm" onClick={getEntity} disabled={loading !== null || !hasCreds || !entityId}>
            {loading === 'entity' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading === 'entity' ? 'Loading…' : 'Get Entity'}
          </Button>
          {pollingMsg && <span className="text-[10px] text-blue-400 font-mono">{pollingMsg}</span>}
        </div>
        <CurlToggle method="GET" url={`${apiBase}/organizations/${entityId || '{id}'}/serviceprofiles/${kybServiceName}/workflows/${selectedWorkflow || '{wf}'}/executions/${workflowExecutionId || '{wfeId}'}`} creds={creds} />
        <CurlToggle method="GET" url={`${apiBase}/organizations/${entityId || '{id}'}`} creds={creds} />
        {workflowResult ? (
          <KycResultSummary wr={workflowResult} fullResponse={fullResult} />
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] italic">No results yet — execute a workflow and click Get Result</p>
        )}
        {resultOrg && (
          <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-1.5 mt-1">
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Organization (from workflow result)</p>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {(() => {
              const r = resultOrg as any
              const name = r?.details?.name?.name ?? r?.details?.name ?? r?.name?.name
              const status = r?.details?.status?.normalized?.code
              return (
                <div className="space-y-1 text-[10px]">
                  {r?.entityId && <div className="flex gap-2"><span className="text-[var(--text-muted)] font-mono w-24 shrink-0">entityId:</span><span className="text-[var(--text-secondary)] font-mono">{String(r.entityId)}</span></div>}
                  {name && <div className="flex gap-2"><span className="text-[var(--text-muted)] font-mono w-24 shrink-0">Legal Name:</span><span className="text-[var(--text-secondary)] font-mono">{String(name)}</span></div>}
                  {status && <div className="flex gap-2"><span className="text-[var(--text-muted)] font-mono w-24 shrink-0">Status:</span><span className={`font-mono font-semibold ${status === 'ACTV' ? 'text-emerald-400' : 'text-amber-400'}`}>{status}</span></div>}
                </div>
              )
            })()}
          </div>
        )}
        {entityResult && (
          <EntityResultPanel entityResult={entityResult} />
        )}
      </Card>

      <InlineWebhookPanel entityId={entityId} customerId={creds.customerId ?? ''} />
    </div>
  )
}

// ── Hosted OneSDK Tab ─────────────────────────────────────────────────────────

export function HostedTab({ creds, addLog }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const [panel, setPanel] = useState<'how' | 'device-risk' | null>(null)
  const [flowId, setFlowId] = useState('idv_daon')
  const [redirectUrl, setRedirectUrl] = useState('https://your-app.com/kyc/complete')
  const [existingEntityId, setExistingEntityId] = useState('')
  const [sendSms, setSendSms] = useState(false)
  const [phoneCode, setPhoneCode] = useState('+61')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [loading, setLoading] = useState(false)
  const [resultLoading, setResultLoading] = useState(false)
  const [hostedUrl, setHostedUrl] = useState<string | null>(null)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [events, setEvents] = useState<{ ts: string; [k: string]: unknown }[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [workflowResult, setWorkflowResult] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const autoFetchedRef = useRef(false)

  // Register postMessage listener
  useEffect(() => {
    function handler(e: MessageEvent) {
      if (e.data?.['X-Frankie-Source'] === 'hosted-onesdk') {
        setEvents(prev => [...prev, { ts: new Date().toLocaleTimeString(), ...e.data }])
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])

  // Auto-fetch result when SUCCESS postMessage arrives
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const last = events[events.length - 1]
    if (last?.type === 'SUCCESS' && entityId && !workflowResult && !autoFetchedRef.current) {
      autoFetchedRef.current = true
      getResult()
    }
  }, [events])

  async function generateUrl() {
    setLoading(true); setHostedUrl(null); setEntityId(null)
    const url = `${apiBase}/individuals/hostedUrl`
    const body: Record<string, unknown> = { consentObtained: true, oneSDKFlowId: flowId, redirectUrl }
    if (existingEntityId.trim()) body.entityId = existingEntityId.trim()
    if (sendSms && phoneNumber.trim()) {
      body.sendSMS = true
      body.phoneNumber = phoneNumber.trim()
      body.phoneCode = phoneCode
      if (recipientName.trim()) body.recipientName = recipientName.trim()
    }
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl('/individuals/hostedUrl'), { method: 'POST', headers: buildHeaders(creds, true), body: JSON.stringify(body) })
      const durationMs = Date.now() - start
      const data = await res.json() as { entityId?: string; url?: string }
      addLog({ method: 'POST', url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation: 'Generate Hosted OneSDK URL — returns { entityId, url }' })
      if (res.ok && data?.url) { setHostedUrl(data.url); setEntityId(data.entityId ?? null) }
    } catch { /* logged */ } finally { setLoading(false) }
  }

  async function getResult() {
    if (!entityId) return
    setResultLoading(true)
    const url = `${apiBase}/individuals/${entityId}`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl(`/individuals/${entityId}`), { method: 'GET', headers: buildHeaders(creds) })
      const durationMs = Date.now() - start
      const data = await res.json() as { workflowResult?: unknown; serviceProfiles?: unknown[] }
      addLog({ method: 'GET', url, status: res.status, durationMs, responseBody: data, annotation: 'Fetch individual record after hosted verification' })
      if (res.ok) {
        setFullResult(data)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const wr = (data as any)?.workflowResult ?? (data as any)?.serviceProfiles?.[0]?.workflowSummaries?.[0] ?? null
        setWorkflowResult(wr)
      }
    } catch { /* logged */ } finally { setResultLoading(false) }
  }

  function reset() { setHostedUrl(null); setEntityId(null); setEvents([]); setWorkflowResult(null); setFullResult(null); autoFetchedRef.current = false }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 justify-end">
        <button onClick={() => setPanel(p => p === 'how' ? null : 'how')} className={tabPanelBtn(panel === 'how')}>
          <Info size={11} /> How it works
        </button>
        <button onClick={() => setPanel(p => p === 'device-risk' ? null : 'device-risk')} className={tabPanelBtn(panel === 'device-risk')}>
          <Shield size={11} /> Device Risk Testing
        </button>
      </div>
      {panel === 'how' && <Card className="p-4"><HowItWorksPanel tabId="hosted" /></Card>}
      {panel === 'device-risk' && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Shield size={13} className="text-violet-400" />
            <p className="text-[11px] font-semibold text-[var(--text-primary)]">Device Risk &amp; Fraud Detection</p>
          </div>
          <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
            When the hosted iframe loads, FrankieOne automatically initialises the <strong>Sardine device SDK</strong> in the background — invisible to the user. It collects a device fingerprint and analyses risk signals throughout the session. Results appear in the <code className="font-mono text-violet-400 text-[9px]">FRAUD</code> step of the workflow result.
          </p>
          <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
                  <th className="px-3 py-1.5 text-left font-semibold text-[var(--text-muted)] uppercase tracking-wide text-[9px]">Check Type</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-[var(--text-muted)] uppercase tracking-wide text-[9px]">What it detects</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-[var(--text-muted)] uppercase tracking-wide text-[9px]">How to trigger in testing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-color)]">
                <tr>
                  <td className="px-3 py-2 font-mono text-violet-400">FRAUD_DEVICE</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">Emulated device, VPN/proxy, device reputation, browser fingerprint</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">Open iframe URL in Chrome with DevTools device emulation ON (Ctrl+Shift+M)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-violet-400">FRAUD_IP_ADDRESS</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">IP reputation, datacenter/VPN IP, geo anomalies</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">Use a VPN or open the hosted URL on a server/cloud IP</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-violet-400">FRAUD_EMAIL_ADDRESS</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">Disposable email, known fraud email addresses</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">Submit with a disposable email domain (e.g. mailinator.com)</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-mono text-violet-400">FRAUD_PHONE_NUMBER</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">VOIP numbers, burner phone signals</td>
                  <td className="px-3 py-2 text-[var(--text-secondary)]">Submit with a known VOIP number pattern</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-1">
            <p className="text-[10px] font-semibold text-amber-400">Account configuration required</p>
            <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
              Sardine device risk must be enabled by FrankieOne for your account. If the FRAUD step is absent from your results, contact your Customer Success rep. If it is present, check the <span className="font-mono text-violet-400">Fraud / Device Risk</span> section in the result below.
            </p>
          </div>
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3 space-y-1">
            <p className="text-[10px] font-semibold text-[var(--text-secondary)]">Testing emulator detection step-by-step</p>
            <ol className="list-decimal list-inside space-y-0.5 text-[10px] text-[var(--text-muted)] pl-1">
              <li>Generate a hosted URL below</li>
              <li>Click <span className="font-mono text-violet-400">Open in new tab</span> to copy the URL</li>
              <li>Open a new Chrome window → press F12 → click the device icon (Toggle device toolbar)</li>
              <li>Paste the hosted URL into the address bar and complete the flow</li>
              <li>Come back here and click <span className="font-mono text-violet-400">Get Result</span> — check for <code className="font-mono">device.isEmulated: true</code></li>
            </ol>
          </div>
        </Card>
      )}

      {/* Step 1 — Generate URL */}
      <Card className="p-3 space-y-3">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 1 — Generate Hosted URL</p>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">POST {apiBase}/individuals/hostedUrl</p>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Flow ID <span className="text-red-400">*</span></label>
            <select value={flowId} onChange={e => setFlowId(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
              {HOSTED_FLOWS.map(f => <option key={f.id} value={f.id}>{f.id} — {f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Redirect URL <span className="text-red-400">*</span></label>
            <input value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="https://your-app.com/kyc/complete" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Entity ID <span className="text-[var(--text-muted)] normal-case font-normal">(optional)</span></label>
            <input value={existingEntityId} onChange={e => setExistingEntityId(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="ent_existing_id — omit to create new" />
          </div>
          <div className="border-t border-[var(--border-color)] pt-2">
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)} className="rounded" />
              <span className="text-[10px] text-[var(--text-secondary)] font-semibold">Send link via SMS</span>
              <span className="text-[9px] text-[var(--text-muted)]">(sendSMS: true)</span>
            </label>
            {sendSms && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2">
                  <div className="w-24 shrink-0">
                    <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Code <span className="text-red-400">*</span></label>
                    <input value={phoneCode} onChange={e => setPhoneCode(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="+61" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Phone Number <span className="text-red-400">*</span></label>
                    <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="0412345678" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Recipient Name <span className="text-[var(--text-muted)] normal-case font-normal">(optional)</span></label>
                  <input value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="Jane Smith" />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={generateUrl} disabled={loading || !hasCreds || !flowId || !redirectUrl || (sendSms && !phoneNumber.trim())}>
            {loading ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading ? 'Generating…' : 'Generate URL'}
          </Button>
          {(hostedUrl || entityId) && <button onClick={reset} className="text-[10px] text-[var(--text-muted)] hover:text-red-400 flex items-center gap-1"><X size={10} /> Reset</button>}
          {!hasCreds && <p className="text-[10px] text-amber-400">Credentials required.</p>}
        </div>
        <CurlToggle method="POST" url={`${apiBase}/individuals/hostedUrl`} creds={creds} body={{ consentObtained: true, oneSDKFlowId: flowId, redirectUrl, ...(existingEntityId.trim() ? { entityId: existingEntityId.trim() } : {}), ...(sendSms && phoneNumber.trim() ? { sendSMS: true, phoneNumber: phoneNumber.trim(), phoneCode, ...(recipientName.trim() ? { recipientName: recipientName.trim() } : {}) } : {}) }} />
        {entityId && (
          <div className="flex items-center gap-2 text-[10px]">
            <span className="text-[var(--text-muted)]">entityId:</span>
            <code className="font-mono text-emerald-400">{entityId}</code>
            <CopyBtn text={entityId} />
          </div>
        )}
        {hostedUrl && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[var(--text-muted)]">Hosted URL:</span>
              <CopyBtn text={hostedUrl} />
            </div>
            <a href={hostedUrl} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-violet-400 break-all hover:underline block">{hostedUrl}</a>
          </div>
        )}
      </Card>

      {/* Step 2 — iframe */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 2 — User Verification (iframe)</p>
        <p className="text-[10px] text-[var(--text-muted)]">FrankieOne handles consent, document capture, liveness, biometrics inside the iframe.</p>
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden bg-[var(--bg-primary)]">
          <div className="px-3 py-2 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] flex items-center justify-between">
            <p className="text-[10px] font-semibold text-[var(--text-secondary)]">Hosted Verification iframe</p>
            <div className="flex items-center gap-3">
              {hostedUrl && <p className="text-[10px] text-[var(--text-muted)] font-mono">{new URL(hostedUrl).host}</p>}
              {hostedUrl && (
                <a href={hostedUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-violet-500/15 text-violet-400 border border-violet-500/30 hover:bg-violet-500/25 transition-colors">
                  ↗ Open in new tab <span className="text-[9px] text-violet-300/60">(for device testing)</span>
                </a>
              )}
            </div>
          </div>
          {hostedUrl ? (
            <iframe src={hostedUrl} allow="camera;fullscreen;accelerometer;gyroscope;magnetometer;geolocation;microphone;" className="w-full" style={{ minHeight: 620, border: 0 }} title="FrankieOne Hosted Verification" />
          ) : (
            <div className="flex items-center justify-center py-16 text-[11px] text-[var(--text-muted)] italic">
              Generate a hosted URL above to load the iframe here
            </div>
          )}
        </div>
      </Card>

      {/* Step 3 — postMessage events */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 3 — postMessage Events</p>
        <p className="text-[10px] text-[var(--text-muted)]">When the user completes verification, the iframe fires a postMessage with <code className="font-mono text-amber-400">X-Frankie-Source: hosted-onesdk</code>.</p>
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-[var(--text-secondary)]">postMessage Events</span>
              {events.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 text-[9px] font-mono">{events.length}</span>}
            </div>
            {events.length > 0 && <button onClick={() => setEvents([])} className="text-[10px] text-[var(--text-muted)] hover:text-red-400">Clear</button>}
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-[var(--border-color)]">
            {events.length === 0 ? (
              <p className="text-[10px] text-[var(--text-muted)] p-3 italic">{hostedUrl ? 'Iframe loaded — complete flow to see events' : 'No events yet — generate URL and complete flow'}</p>
            ) : events.map((ev, i) => (
              <div key={i} className="px-3 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[9px] text-[var(--text-muted)]">{ev.ts}</span>
                  {ev.type !== undefined && <span className="px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 text-[9px] font-mono">{String(ev.type)}</span>}
                </div>
                <pre className="text-[9px] text-[var(--text-secondary)] font-mono overflow-auto">{JSON.stringify(ev, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Step 4 — Get Result */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 4 — Get Result</p>
        <p className="text-[10px] text-[var(--text-muted)]">After the postMessage arrives, fetch the individual entity to get the full workflowResult. Auto-fetches on SUCCESS event.</p>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">GET {apiBase}/individuals/{entityId || '{entityId}'}</p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={getResult} disabled={resultLoading || !entityId}>
            {resultLoading ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{resultLoading ? 'Loading…' : 'Get Result'}
          </Button>
          {!entityId && <span className="text-[10px] text-[var(--text-muted)] italic">Generate URL first to get entityId</span>}
        </div>
        <CurlToggle method="GET" url={`${apiBase}/individuals/${entityId || '{entityId}'}`} creds={creds} />
        {workflowResult ? (
          <div className="space-y-2">
            <FraudStepSection wr={workflowResult} />
            <KycResultSummary wr={workflowResult} fullResponse={fullResult} />
          </div>
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] italic">No results yet — complete verification and click Get Result</p>
        )}
      </Card>

      <InlineWebhookPanel entityId={entityId} customerId={creds.customerId ?? ''} />
    </div>
  )
}

// ── Hosted V1 Event Summary ───────────────────────────────────────────────────

const V1_CHECK_LABELS: Record<string, string> = {
  blacklist: 'Blacklist / Blocklist',
  id: 'Government ID',
  two_plus: '2+2 (Credit Header)',
  idvalidate: 'Document Validation (IDV)',
  pep: 'PEP / Sanctions',
  aml: 'AML',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function HostedV1EventSummary({ events }: { events: { ts: string; [k: string]: unknown }[] }) {
  const [rawOpen, setRawOpen] = useState(false)

  // Find the latest result event — matches any flow's completion event e.g. idv:complete, manual_kyc:success, idv:failed
  function isResultEvent(ev: { [k: string]: unknown }) {
    const name = String(ev.event ?? '')
    return name.endsWith(':success') || name.endsWith(':complete') || name.endsWith(':failed') || name.endsWith(':error')
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resultEvent = [...events].reverse().find((e: any) => isResultEvent(e))
  if (!resultEvent) return null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload = (resultEvent as any).payload as Record<string, any> | undefined
  const eventName = String(resultEvent.event ?? '')
  const isSuccess = eventName.endsWith(':success') || eventName.endsWith(':complete')

  // If no structured payload, show raw event for debugging
  if (!payload) {
    return (
      <div className="mt-2 border border-amber-500/30 rounded-lg p-3 bg-amber-500/5 space-y-1">
        <div className="flex items-center gap-2">
          <StatusBadge status={isSuccess ? 'PASS' : 'FAIL'} />
          <span className="text-[10px] font-mono text-[var(--text-muted)]">{eventName}</span>
          <span className="text-[10px] text-amber-400 italic">— no payload in event (share raw event for debugging)</span>
        </div>
        <JsonView data={resultEvent} />
      </div>
    )
  }

  const status: string = payload.status?.key ?? (isSuccess ? 'PASS' : 'FAIL')
  const riskClass: string | undefined = payload.risk?.class
  const riskLevel: number | null = payload.risk?.level ?? null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const checkResults: any[] = payload.checkResults ?? []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const alerts: any[] = payload.alertList ?? []
  const kycMethod: string | undefined = payload.kycMethod
  const checkDate: string | undefined = payload.checkDate

  const RISK_CLASS_CLS: Record<string, string> = {
    LOW: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    MEDIUM: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    HIGH: 'bg-red-500/15 text-red-400 border-red-500/30',
    UNACCEPTABLE: 'bg-red-700/20 text-red-300 border-red-600/40',
  }

  const CHECK_RESULT_CLS: Record<string, string> = {
    PASS: 'text-emerald-400',
    FAIL: 'text-red-400',
    UNCHECKED: 'text-[var(--text-muted)]',
    REVIEW: 'text-amber-400',
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Outcome */}
      <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-2">
        <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Outcome</p>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={status} />
          {riskClass && (
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border ${RISK_CLASS_CLS[riskClass] ?? 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border-[var(--border-color)]'}`}>
              {riskClass} RISK
            </span>
          )}
          {alerts.length > 0 && alerts.map((a, i) => (
            <span key={i} className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border bg-amber-500/10 text-amber-400 border-amber-500/20">
              alert: {a.term}
            </span>
          ))}
        </div>
        {riskLevel !== null && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-[var(--text-muted)] font-mono">Risk Score</span>
              <span className={`font-bold font-mono text-[11px] ${riskLevel >= 80 ? 'text-red-400' : riskLevel >= 50 ? 'text-amber-400' : 'text-emerald-400'}`}>{riskLevel} / 100</span>
            </div>
            <div className="h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden">
              <div className={`h-full rounded-full ${riskLevel >= 80 ? 'bg-red-500' : riskLevel >= 50 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${riskLevel}%` }} />
            </div>
          </div>
        )}
        <div className="flex items-center gap-4 flex-wrap text-[10px] font-mono text-[var(--text-muted)]">
          {kycMethod && <span>Method: <span className="text-[var(--text-secondary)]">{kycMethod}</span></span>}
          {checkDate && <span>Checked: <span className="text-[var(--text-secondary)]">{new Date(checkDate).toLocaleString()}</span></span>}
        </div>
      </div>

      {/* If no parsed check results, show raw payload so we can see the structure */}
      {checkResults.length === 0 && (
        <div className="border border-amber-500/30 rounded-lg p-3 bg-amber-500/5">
          <p className="text-[10px] text-amber-400 font-semibold mb-1">No checkResults in event payload — raw payload:</p>
          <JsonView data={payload} />
        </div>
      )}

      {/* Check Results */}
      {checkResults.length > 0 && (
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide px-3 py-2 border-b border-[var(--border-color)]">Check Results</p>
          <table className="w-full text-[10px]">
            <tbody>
              {checkResults.map((check, i) => {
                const resultType: string = check.result?.type ?? 'UNCHECKED'
                const label = V1_CHECK_LABELS[check.type] ?? check.type
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const issues: any[] = (check.issues ?? []).filter((iss: any) => iss.code)
                return (
                  <tr key={i} className={`border-t border-[var(--border-color)] first:border-0 ${i % 2 === 1 ? 'bg-[var(--bg-primary)]' : ''}`}>
                    <td className="px-3 py-2 font-mono text-[var(--text-secondary)] w-40">{label}</td>
                    <td className="px-3 py-2">
                      <span className={`font-bold ${CHECK_RESULT_CLS[resultType] ?? 'text-[var(--text-muted)]'}`}>{resultType}</span>
                    </td>
                    <td className="px-3 py-2 text-[var(--text-muted)] italic">
                      {issues.map((iss, j) => (
                        <span key={j}>{iss.description ?? iss.code}</span>
                      ))}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Raw payload */}
      <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
        <button onClick={() => setRawOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-tertiary)] transition-colors">
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Raw Event Payload</span>
          {rawOpen ? <ChevronUp size={10} className="text-[var(--text-muted)]" /> : <ChevronDown size={10} className="text-[var(--text-muted)]" />}
        </button>
        {rawOpen && (
          <div className="border-t border-[var(--border-color)] bg-[var(--bg-primary)] p-2 max-h-64 overflow-y-auto">
            <JsonView data={payload} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Hosted V1 Entity Display ──────────────────────────────────────────────────
// GET /entity/{id} in V1 returns entity profile only — check results (entityProfileResult,
// checkRisk, checkSummary) are only in the synchronous POST response which the hosted UI
// calls internally. We display what IS available from the entity GET.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function HostedV1EntityDisplay({ data, fullResponse }: { data: any; fullResponse: unknown }) {
  const [rawOpen, setRawOpen] = useState(false)
  const entity = data?.entity
  if (!entity) return null

  const name = entity.name
  const dob: string | undefined = entity.dateOfBirth?.dateOfBirth
  const profile: string | undefined = entity.entityProfile
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flags: { flag: string; value: unknown }[] = entity.flags ?? []
  const verificationRuns = flags.find(f => f.flag === 'verification_runs')?.value
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const docs: any[] = entity.identityDocs ?? []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const addresses: any[] = entity.addresses ?? []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const extraData: { kvpKey: string; kvpValue: string }[] = entity.extraData ?? []
  const kycMethod = extraData.find(e => e.kvpKey === 'kyc.method')?.kvpValue
  const ageGroup = extraData.find(e => e.kvpKey === 'age_group')?.kvpValue

  return (
    <div className="space-y-2 mt-2">
      {/* Status note */}
      <div className="border border-amber-500/30 rounded-lg p-3 bg-amber-500/5 space-y-1">
        <p className="text-[10px] font-semibold text-amber-400">V1 Hosted — entity profile only</p>
        <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
          For the hosted flow, detailed check results (risk score, check breakdown) are processed internally by FrankieOne and not returned by <code className="font-mono text-[9px]">GET /entity</code>.
          Check the <strong>FrankieOne portal</strong> to see the full KYC result for this entity.
        </p>
      </div>

      {/* Entity summary */}
      <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-2">
        <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Entity</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
          {name?.displayName && (
            <><span className="text-[var(--text-muted)] font-mono">Name</span><span className="text-[var(--text-secondary)] font-medium capitalize">{name.displayName}</span></>
          )}
          {dob && (
            <><span className="text-[var(--text-muted)] font-mono">Date of Birth</span><span className="text-[var(--text-secondary)] font-mono">{dob}</span></>
          )}
          {profile && (
            <><span className="text-[var(--text-muted)] font-mono">Entity Profile</span><span className="text-[var(--text-secondary)] font-mono">{profile}</span></>
          )}
          {kycMethod && (
            <><span className="text-[var(--text-muted)] font-mono">KYC Method</span><span className="text-[var(--text-secondary)]">{kycMethod}</span></>
          )}
          {ageGroup && (
            <><span className="text-[var(--text-muted)] font-mono">Age Group</span><span className="text-[var(--text-secondary)]">{ageGroup}</span></>
          )}
          {verificationRuns !== undefined && (
            <><span className="text-[var(--text-muted)] font-mono">Verification Runs</span><span className={`font-bold font-mono ${Number(verificationRuns) > 0 ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>{String(verificationRuns)}</span></>
          )}
          <><span className="text-[var(--text-muted)] font-mono">Entity ID</span><span className="text-[var(--text-secondary)] font-mono text-[9px]">{entity.entityId}</span></>
        </div>
      </div>

      {/* Identity docs */}
      {docs.length > 0 && (
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide px-3 py-2 border-b border-[var(--border-color)]">Identity Documents</p>
          <table className="w-full text-[10px]">
            <tbody>
              {docs.map((doc, i) => (
                <tr key={i} className={`border-t border-[var(--border-color)] first:border-0 ${i % 2 === 1 ? 'bg-[var(--bg-primary)]' : ''}`}>
                  <td className="px-3 py-1.5 font-mono text-[var(--text-secondary)]">{doc.idType}</td>
                  <td className="px-3 py-1.5 font-mono text-[var(--text-muted)]">{doc.country}{doc.region ? ` / ${doc.region}` : ''}</td>
                  <td className="px-3 py-1.5 font-mono text-[var(--text-secondary)]">{doc.idNumber}</td>
                  {doc.idExpiry && <td className="px-3 py-1.5 font-mono text-[var(--text-muted)]">exp {doc.idExpiry}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Address */}
      {addresses.length > 0 && (
        <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-1">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Address</p>
          {addresses.map((a, i) => (
            <p key={i} className="text-[10px] text-[var(--text-secondary)]">{a.longForm}</p>
          ))}
        </div>
      )}

      {/* Raw JSON */}
      <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
        <button onClick={() => setRawOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-tertiary)] transition-colors">
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Raw JSON Response</span>
          {rawOpen ? <ChevronUp size={10} className="text-[var(--text-muted)]" /> : <ChevronDown size={10} className="text-[var(--text-muted)]" />}
        </button>
        {rawOpen && (
          <div className="border-t border-[var(--border-color)] bg-[var(--bg-primary)] p-2 max-h-64 overflow-y-auto">
            <JsonView data={fullResponse} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Hosted OneSDK V1 Tab ──────────────────────────────────────────────────────

const V1_HOSTED_BASE = 'https://api.kycaml.uat.frankiefinancial.io'
const V1_HOSTED_PATH = '/idv/v2/idvalidate/onboarding-url'

const HOSTED_V1_FLOWS = [
  { id: 'idv', label: 'Biometrics/OCR' },
  { id: 'idv_review', label: 'Biometrics/OCR + Review' },
  { id: 'manual_kyc', label: 'eKYC (form only)' },
  { id: 'doc_upload', label: 'Document Upload (business)' },
  { id: 'individual_doc_upload', label: 'Document Upload (individual)' },
  { id: 'ocr_only', label: 'OCR Only' },
]

export function HostedV1Tab({ creds, addLog }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const v1Creds = { ...creds, apiBase: V1_HOSTED_BASE }
  const [panel, setPanel] = useState<'device-risk' | null>(null)
  const [flowId, setFlowId] = useState('idv')
  const [customerRef, setCustomerRef] = useState(`test-${Date.now()}`)
  const [existingEntityId, setExistingEntityId] = useState('')
  const [manualEntityId, setManualEntityId] = useState('')
  const [sendSms, setSendSms] = useState(false)
  const [phoneCode, setPhoneCode] = useState('+61')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [loading, setLoading] = useState(false)
  const [resultLoading, setResultLoading] = useState(false)
  const [hostedUrl, setHostedUrl] = useState<string | null>(null)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [events, setEvents] = useState<{ ts: string; [k: string]: unknown }[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [workflowResult, setWorkflowResult] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const autoFetchedRef = useRef(false)

  useEffect(() => {
    function handler(e: MessageEvent) {
      if (e.data?.['X-Frankie-Source'] === 'hosted-onesdk') {
        const msg = { ts: new Date().toLocaleTimeString(), ...e.data }
        setEvents(prev => [...prev, msg])
        // Capture entityId from postMessage if present
        if (e.data?.entityId) setEntityId(e.data.entityId as string)
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const last = events[events.length - 1]
    const name = String(last?.event ?? '')
    const isV1Result = name.endsWith(':success') || name.endsWith(':complete') || name.endsWith(':failed') || name.endsWith(':error')
    if (isV1Result && entityId && !workflowResult && !autoFetchedRef.current) {
      autoFetchedRef.current = true
      getResult(entityId)
    }
  }, [events])

  async function generateUrl() {
    setLoading(true); setHostedUrl(null); setEntityId(null)
    const url = `${V1_HOSTED_BASE}${V1_HOSTED_PATH}`
    const body: Record<string, unknown> = { consent: true, flowId, customerRef }
    if (existingEntityId.trim()) body.entity = { entityId: existingEntityId.trim() }
    if (sendSms && phoneNumber.trim()) {
      body.sendSMS = true
      body.phoneNumber = phoneNumber.trim()
      body.phoneCode = phoneCode
      if (recipientName.trim()) body.recipientName = recipientName.trim()
    }
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl(V1_HOSTED_PATH), {
        method: 'POST',
        headers: buildHeaders(v1Creds, true),
        body: JSON.stringify(body),
      })
      const durationMs = Date.now() - start
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await res.json() as any
      addLog({ method: 'POST', url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation: 'V1 Hosted: Generate onboarding URL' })
      const generatedUrl: string | undefined = data?.url ?? data?.onboardingUrl ?? data?.data?.url
      const eid: string | undefined = data?.entityId ?? data?.entity?.entityId
      if (res.ok && generatedUrl) {
        setHostedUrl(generatedUrl)
        if (eid) setEntityId(eid)
      }
    } catch { /* logged */ } finally { setLoading(false) }
  }

  async function getResult(eid?: string) {
    const resolvedId = eid ?? entityId ?? manualEntityId.trim()
    if (!resolvedId) return
    setResultLoading(true)
    const url = `${V1_HOSTED_BASE}/compliance/v1.2/entity/${resolvedId}`
    const start = Date.now()
    try {
      // Use V1_BASE path (api.kycaml.uat.frankiefinancial.io/compliance/v1.2) so proxy routes correctly
      const res = await fetch(proxyUrl(`/entity/${resolvedId}`), {
        method: 'GET',
        headers: buildHeaders({ ...v1Creds, apiBase: V1_BASE }),
      })
      const durationMs = Date.now() - start
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await res.json() as any
      addLog({ method: 'GET', url, status: res.status, durationMs, responseBody: data, annotation: 'V1 Hosted: Fetch entity result' })
      if (res.ok) {
        setFullResult(data)
        // V1 entity response has entityProfileResult/checkRisk/checkSummary at top level — store whole response
        setWorkflowResult(data)
      }
    } catch { /* logged */ } finally { setResultLoading(false) }
  }

  function reset() {
    setHostedUrl(null); setEntityId(null); setEvents([]); setWorkflowResult(null)
    setFullResult(null); setManualEntityId(''); autoFetchedRef.current = false
  }

  const activeEntityId = (entityId ?? manualEntityId.trim()) || null

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 justify-end">
        <button onClick={() => setPanel(p => p === 'device-risk' ? null : 'device-risk')} className={tabPanelBtn(panel === 'device-risk')}>
          <Shield size={11} /> Device Risk Testing
        </button>
      </div>
      {panel === 'device-risk' && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Shield size={13} className="text-violet-400" />
            <p className="text-[11px] font-semibold text-[var(--text-primary)]">Device Risk &amp; Fraud Detection (V1 Hosted)</p>
          </div>
          <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
            Device risk (Sardine) runs automatically inside the iframe if enabled on your account. Complete the hosted flow then check the result for a FRAUD step.
          </p>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-[10px] font-semibold text-amber-400">To test emulator detection</p>
            <ol className="list-decimal list-inside space-y-0.5 text-[10px] text-[var(--text-muted)] pl-1 mt-1">
              <li>Generate a hosted URL below</li>
              <li>Click <span className="font-mono text-violet-400">Open in new tab</span></li>
              <li>Open Chrome DevTools → Toggle device toolbar (Ctrl+Shift+M)</li>
              <li>Paste hosted URL into address bar and complete the flow</li>
              <li>Return here, enter the entity ID and click Get Result</li>
            </ol>
          </div>
        </Card>
      )}

      {/* Step 1 — Generate URL */}
      <Card className="p-3 space-y-3">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 1 — Generate Hosted URL (V1)</p>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">{V1_HOSTED_BASE}{V1_HOSTED_PATH}</p>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Flow ID <span className="text-red-400">*</span></label>
            <select value={flowId} onChange={e => setFlowId(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
              {HOSTED_V1_FLOWS.map(f => <option key={f.id} value={f.id}>{f.id} — {f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Customer Ref <span className="text-red-400">*</span></label>
            <input value={customerRef} onChange={e => setCustomerRef(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="unique-reference-for-this-user" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Entity ID <span className="text-[var(--text-muted)] normal-case font-normal">(optional — existing entity)</span></label>
            <input value={existingEntityId} onChange={e => setExistingEntityId(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="omit to create new" />
          </div>
          <div className="border-t border-[var(--border-color)] pt-2">
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)} className="rounded" />
              <span className="text-[10px] text-[var(--text-secondary)] font-semibold">Send link via SMS</span>
              <span className="text-[9px] text-[var(--text-muted)]">(sendSMS: true)</span>
            </label>
            {sendSms && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2">
                  <div className="w-24 shrink-0">
                    <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Code <span className="text-red-400">*</span></label>
                    <input value={phoneCode} onChange={e => setPhoneCode(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="+61" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Phone Number <span className="text-red-400">*</span></label>
                    <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="0412345678" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Recipient Name <span className="text-[var(--text-muted)] normal-case font-normal">(optional)</span></label>
                  <input value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="Jane Smith" />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={generateUrl} disabled={loading || !hasCreds || !flowId || !customerRef || (sendSms && !phoneNumber.trim())}>
            {loading ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{loading ? 'Generating…' : 'Generate URL'}
          </Button>
          {(hostedUrl || entityId) && <button onClick={reset} className="text-[10px] text-[var(--text-muted)] hover:text-red-400 flex items-center gap-1"><X size={10} /> Reset</button>}
          {!hasCreds && <p className="text-[10px] text-amber-400">Credentials required.</p>}
        </div>
        <CurlToggle method="POST" url={`${V1_HOSTED_BASE}${V1_HOSTED_PATH}`} creds={creds} body={{ consent: true, flowId, customerRef, ...(existingEntityId.trim() ? { entity: { entityId: existingEntityId.trim() } } : {}), ...(sendSms && phoneNumber.trim() ? { sendSMS: true, phoneNumber: phoneNumber.trim(), phoneCode, ...(recipientName.trim() ? { recipientName: recipientName.trim() } : {}) } : {}) }} />
        {entityId && (
          <div className="flex items-center gap-2 text-[10px]">
            <span className="text-[var(--text-muted)]">entityId:</span>
            <code className="font-mono text-emerald-400">{entityId}</code>
            <CopyBtn text={entityId} />
          </div>
        )}
        {hostedUrl && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[var(--text-muted)]">Hosted URL:</span>
              <CopyBtn text={hostedUrl} />
            </div>
            <a href={hostedUrl} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-violet-400 break-all hover:underline block">{hostedUrl}</a>
          </div>
        )}
      </Card>

      {/* Step 2 — iframe */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 2 — User Verification (iframe)</p>
        <p className="text-[10px] text-[var(--text-muted)]">FrankieOne handles consent, document capture, liveness, biometrics inside the iframe.</p>
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden bg-[var(--bg-primary)]">
          <div className="px-3 py-2 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] flex items-center justify-between">
            <p className="text-[10px] font-semibold text-[var(--text-secondary)]">Hosted Verification iframe (V1)</p>
            <div className="flex items-center gap-3">
              {hostedUrl && <p className="text-[10px] text-[var(--text-muted)] font-mono">{new URL(hostedUrl).host}</p>}
              {hostedUrl && (
                <a href={hostedUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-violet-500/15 text-violet-400 border border-violet-500/30 hover:bg-violet-500/25 transition-colors">
                  ↗ Open in new tab <span className="text-[9px] text-violet-300/60">(for device testing)</span>
                </a>
              )}
            </div>
          </div>
          {hostedUrl ? (
            <iframe src={hostedUrl} allow="camera;fullscreen;accelerometer;gyroscope;magnetometer;geolocation;microphone;" className="w-full" style={{ minHeight: 620, border: 0 }} title="FrankieOne Hosted Verification V1" />
          ) : (
            <div className="flex items-center justify-center py-16 text-[11px] text-[var(--text-muted)] italic">
              Generate a hosted URL above to load the iframe here
            </div>
          )}
        </div>
      </Card>

      {/* Step 3 — postMessage events */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 3 — postMessage Events &amp; Result</p>
        <p className="text-[10px] text-[var(--text-muted)]">The iframe fires postMessages as the user progresses. The final event (e.g. <code className="font-mono text-amber-400">idv:complete</code>, <code className="font-mono text-amber-400">manual_kyc:success</code>, <code className="font-mono text-amber-400">idv:failed</code>) contains the full KYC result and triggers an auto-fetch.</p>
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-[var(--text-secondary)]">postMessage Events</span>
              {events.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 text-[9px] font-mono">{events.length}</span>}
            </div>
            {events.length > 0 && <button onClick={() => setEvents([])} className="text-[10px] text-[var(--text-muted)] hover:text-red-400">Clear</button>}
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-[var(--border-color)]">
            {events.length === 0 ? (
              <p className="text-[10px] text-[var(--text-muted)] p-3 italic">{hostedUrl ? 'Iframe loaded — complete flow to see events' : 'No events yet — generate URL and complete flow'}</p>
            ) : events.map((ev, i) => (
              <div key={i} className="px-3 py-1.5 flex items-center gap-2">
                <span className="font-mono text-[9px] text-[var(--text-muted)] w-20 shrink-0">{ev.ts}</span>
                {ev.event !== undefined
                  ? <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-medium ${String(ev.event).includes('failed') ? 'bg-red-500/15 text-red-400' : String(ev.event).includes('complete') ? 'bg-emerald-500/15 text-emerald-400' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'}`}>{String(ev.event)}</span>
                  : ev.type !== undefined && <span className="px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 text-[9px] font-mono">{String(ev.type)}</span>
                }
              </div>
            ))}
          </div>
        </div>
        <HostedV1EventSummary events={events} />
      </Card>

      {/* Step 4 — Get Result */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 4 — Get Result</p>
        <p className="text-[10px] text-[var(--text-muted)]">
          Fetch entity result via V1 API. Auto-fetches on SUCCESS event if entityId is available.
          If V1 hosted response did not include an entityId, enter it manually below.
        </p>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">{V1_BASE}/entity/{activeEntityId || '{entityId}'}</p>
        {!entityId && (
          <div>
            <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide font-semibold block mb-0.5">Entity ID <span className="text-red-400">*</span></label>
            <input value={manualEntityId} onChange={e => setManualEntityId(e.target.value)} className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2.5 py-1.5 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" placeholder="Enter entity ID from portal or postMessage" />
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => getResult()} disabled={resultLoading || !activeEntityId}>
            {resultLoading ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{resultLoading ? 'Loading…' : 'Get Result'}
          </Button>
          {!activeEntityId && <span className="text-[10px] text-[var(--text-muted)] italic">Entity ID required</span>}
        </div>
        <CurlToggle method="GET" url={`${V1_BASE}/entity/${activeEntityId || '{entityId}'}`} creds={creds} />
        {workflowResult ? (
          <HostedV1EntityDisplay data={workflowResult} fullResponse={fullResult} />
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] italic">No results yet — complete verification and click Get Result</p>
        )}
      </Card>

      <InlineWebhookPanel entityId={activeEntityId} customerId={creds.customerId ?? ''} />
    </div>
  )
}

// ── Embedded OneSDK Tab ───────────────────────────────────────────────────────

type SdkStatus = 'idle' | 'getting-token' | 'initialising' | 'ready' | 'error'

export function EmbeddedTab({ creds, addLog }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const machineBase = creds.machineBase || DEFAULT_CREDS.machineBase
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const [panel, setPanel] = useState<'how' | null>(null)
  const [flowType, setFlowType] = useState('idv')
  const [sdkMode, setSdkMode] = useState<'development' | 'dummy'>('development')
  const [customerRef, setCustomerRef] = useState(`ft-${Date.now()}`)
  const [sdkStatus, setSdkStatus] = useState<SdkStatus>('idle')
  const [sdkError, setSdkError] = useState<string | null>(null)
  const [events, setEvents] = useState<{ ts: string; eventName: string; args: unknown }[]>([])
  const [sessionInfo, setSessionInfo] = useState<{ token?: string; entityId?: string; expiry?: string } | null>(null)
  const [resultLoading, setResultLoading] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [workflowResult, setWorkflowResult] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const initRef = useRef(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sdkRef = useRef<any>(null)

  useEffect(() => {
    return () => {
      if (sdkRef.current) { try { sdkRef.current.unmount?.() } catch { /* ignore */ }; sdkRef.current = null }
      initRef.current = false
      const c = document.getElementById('onesdk-container'); if (c) c.innerHTML = ''
    }
  }, [])

  function appendEvent(eventName: string, args: unknown) {
    setEvents(prev => [...prev, { ts: new Date().toLocaleTimeString(), eventName, args }])
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function mountIdvFlow(instance: any) {
    const welcome = instance.component('form', { name: 'WELCOME', type: 'ocr' })
    const consent = instance.component('form', { name: 'CONSENT' })
    const idvFlow = instance.flow(flowType)
    welcome.mount(document.getElementById('onesdk-container'))
    welcome.on('form:welcome:loaded', () => appendEvent('form:welcome:loaded', {}))
    welcome.on('form:welcome:ready', () => { appendEvent('form:welcome:ready', {}); welcome.unmount(); consent.mount(document.getElementById('onesdk-container')) })
    consent.on('form:consent:loaded', () => appendEvent('form:consent:loaded', {}))
    consent.on('form:consent:ready', () => { appendEvent('form:consent:ready', {}); consent.unmount(); idvFlow.mount(document.getElementById('onesdk-container')) })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    instance.on('detection_complete', (args: any) => { appendEvent('detection_complete', args); instance.individual().submit({ verify: true }).catch(() => {}) })
    instance.on('results', (args: unknown) => appendEvent('results', args))
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function mountEkycFlow(instance: any) {
    const welcome = instance.component('form', { name: 'WELCOME', type: 'manual' })
    const consent = instance.component('form', { name: 'CONSENT' })
    const personal = instance.component('form', { name: 'PERSONAL' })
    const docComp = instance.component('form', { name: 'DOCUMENT' })
    const review = instance.component('form', { name: 'REVIEW', verify: true })
    const retry = instance.component('form', { name: 'RETRY' })
    welcome.mount(document.getElementById('onesdk-container'))
    welcome.on('form:welcome:loaded', () => appendEvent('form:welcome:loaded', {}))
    welcome.on('form:welcome:ready', () => { appendEvent('form:welcome:ready', {}); welcome.unmount(); consent.mount(document.getElementById('onesdk-container')) })
    consent.on('form:consent:ready', () => { appendEvent('form:consent:ready', {}); consent.unmount(); personal.mount(document.getElementById('onesdk-container')) })
    personal.on('form:personal:ready', () => { appendEvent('form:personal:ready', {}); personal.unmount(); docComp.mount(document.getElementById('onesdk-container')) })
    docComp.on('form:document:ready', () => { appendEvent('form:document:ready', {}); docComp.unmount(); review.mount(document.getElementById('onesdk-container')) })
    review.on('form:review:result:success', (a: unknown) => appendEvent('form:review:result:success', a))
    review.on('form:review:result:failed', (a: unknown) => { appendEvent('form:review:result:failed', a); review.unmount(); retry.mount(document.getElementById('onesdk-container')) })
    review.on('form:review:result:pending', (a: unknown) => appendEvent('form:review:result:pending', a))
  }

  async function handleStart() {
    if (initRef.current) return
    initRef.current = true

    let token: string | undefined
    let entityId: string | undefined

    if (sdkMode === 'development') {
      setSdkStatus('getting-token')
      const url = `${machineBase}/auth/v2/machine-session`
      const start = Date.now()
      try {
        const res = await fetch('/api/frankie-machine-session', {
          method: 'POST',
          headers: buildMachineHeaders(creds, true),
          body: JSON.stringify({ reference: customerRef }),
        })
        const durationMs = Date.now() - start
        const data = await res.json() as { token?: string; entityId?: string; expiry?: string }
        addLog({ method: 'POST', url, status: res.status, durationMs, requestBody: { reference: customerRef }, responseBody: data, annotation: 'Mint one-time SDK session token — server-side credential exchange' })
        if (!res.ok || !data?.token) { setSdkStatus('error'); setSdkError('Failed to get session token — check credentials'); initRef.current = false; return }
        token = data.token; entityId = data.entityId
        setSessionInfo({ token: data.token, entityId: data.entityId, expiry: data.expiry })
      } catch { setSdkStatus('error'); setSdkError('Session token request failed'); initRef.current = false; return }
    }

    setSdkStatus('initialising')
    try {
      const OneSdkModule = await import('@frankieone/one-sdk')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const OneSdk = (OneSdkModule as any).default
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const instance = await (OneSdk as any)({
        mode: sdkMode,
        ...(sdkMode === 'development' ? { session: { token, appReference: 'dealhub-ft-v1' } } : {}),
        recipe: { form: { provider: { name: 'react', googleApiKey: '' } } },
        devtools: true,
      })
      sdkRef.current = instance
      setSdkStatus('ready')
      const container = document.getElementById('onesdk-container'); if (container) container.innerHTML = ''
      instance.on('*', ({ eventName, arguments: args }: { eventName: string; arguments: unknown }) => appendEvent(eventName, args))
      if (isEkycFlow(flowType)) mountEkycFlow(instance)
      else mountIdvFlow(instance)
      void entityId // suppress unused warning — it's in sessionInfo
    } catch (e) {
      setSdkStatus('error'); setSdkError(e instanceof Error ? e.message : String(e)); initRef.current = false
    }
  }

  function resetSdk() {
    if (sdkRef.current) { try { sdkRef.current.unmount?.() } catch { /* ignore */ }; sdkRef.current = null }
    initRef.current = false; setSdkStatus('idle'); setSdkError(null); setEvents([]); setSessionInfo(null)
    const c = document.getElementById('onesdk-container'); if (c) c.innerHTML = ''
    setWorkflowResult(null); setFullResult(null)
  }

  async function getResult() {
    const eid = sessionInfo?.entityId; if (!eid) return
    setResultLoading(true)
    const url = `${apiBase}/individuals/${eid}`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl(`/individuals/${eid}`), { method: 'GET', headers: buildHeaders(creds) })
      const durationMs = Date.now() - start
      const data = await res.json() as { workflowResult?: unknown; serviceProfiles?: unknown[] }
      addLog({ method: 'GET', url, status: res.status, durationMs, responseBody: data, annotation: 'Fetch individual record after SDK verification' })
      if (res.ok) {
        setFullResult(data)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setWorkflowResult((data as any)?.workflowResult ?? (data as any)?.serviceProfiles?.[0]?.workflowSummaries?.[0] ?? null)
      }
    } catch { /* logged */ } finally { setResultLoading(false) }
  }

  const statusDotCls: Record<SdkStatus, string> = {
    idle: 'bg-gray-500', 'getting-token': 'bg-amber-400 animate-pulse', initialising: 'bg-amber-400 animate-pulse', ready: 'bg-emerald-500', error: 'bg-red-500',
  }
  const statusLabel: Record<SdkStatus, string> = {
    idle: 'Idle', 'getting-token': 'Getting token…', initialising: 'Initialising…', ready: 'Ready', error: 'Error',
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 justify-end">
        <button onClick={() => setPanel(p => p === 'how' ? null : 'how')} className={tabPanelBtn(panel === 'how')}>
          <Info size={11} /> How it works
        </button>
      </div>
      {panel === 'how' && <Card className="p-4"><HowItWorksPanel tabId="embedded" /></Card>}

      {/* Step 1 — Config & Token */}
      <Card className="p-3 space-y-3">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 1 — Configuration &amp; Session Token</p>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] text-[var(--text-muted)]">SDK Flow</label>
            <select value={flowType} onChange={e => setFlowType(e.target.value)} className="w-full mt-0.5 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
              {EMBEDDED_FLOWS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
            <p className="text-[9px] text-[var(--text-muted)] mt-0.5">{EMBEDDED_FLOWS.find(f => f.id === flowType)?.note}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[var(--text-muted)]">SDK Mode</label>
              <select value={sdkMode} onChange={e => setSdkMode(e.target.value as 'development' | 'dummy')} className="w-full mt-0.5 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
                <option value="development">development — real session token</option>
                <option value="dummy">dummy — no credentials needed</option>
              </select>
            </div>
            {sdkMode === 'development' && (
              <div>
                <label className="text-[10px] text-[var(--text-muted)]">Customer Reference</label>
                <input value={customerRef} onChange={e => setCustomerRef(e.target.value)} className="w-full mt-0.5 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary" />
              </div>
            )}
          </div>
          {/* Flow sequence info */}
          <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded p-2 text-[9px]">
            <p className="font-semibold text-[var(--text-muted)] mb-1">Component mounting sequence:</p>
            <div className="flex items-center gap-1 flex-wrap font-mono text-[var(--text-muted)]">
              {isEkycFlow(flowType) ? (
                <>
                  {['WELCOME','CONSENT','PERSONAL','DOCUMENT'].map((c, i) => <span key={c}><span className="px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400">{c}</span>{i < 3 && ' → '}</span>)}
                  <span> → <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">REVIEW (verify)</span></span>
                </>
              ) : (
                <>
                  <span className="px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400">WELCOME</span>
                  <span> → </span>
                  <span className="px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400">CONSENT</span>
                  <span> → </span>
                  <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">flow('{flowType}')</span>
                  <span> → </span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">detection_complete</span>
                  <span> → submit()</span>
                </>
              )}
            </div>
          </div>
        </div>
        {sdkMode === 'development' && <p className="text-[9px] text-[var(--text-muted)] font-mono">POST {machineBase}/auth/v2/machine-session</p>}
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="primary" size="sm" onClick={handleStart} disabled={sdkStatus !== 'idle' || (!hasCreds && sdkMode === 'development')}>
            {(sdkStatus === 'getting-token' || sdkStatus === 'initialising') ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
            {sdkStatus === 'idle' ? (sdkMode === 'dummy' ? 'Start SDK (dummy)' : 'Get Token & Start SDK') : statusLabel[sdkStatus]}
          </Button>
          <Button variant="secondary" size="sm" onClick={resetSdk}>Reset</Button>
          <div className="flex items-center gap-1.5 text-[10px]">
            <span className={`inline-block w-2 h-2 rounded-full ${statusDotCls[sdkStatus]}`} />
            <span className="text-[var(--text-muted)]">{statusLabel[sdkStatus]}</span>
          </div>
        </div>
        {sdkError && <p className="text-[10px] text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2 py-1">{sdkError}</p>}
        {sessionInfo?.entityId && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[10px]">
            <span className="text-emerald-400 font-semibold">Token received</span>
            <span className="text-[var(--text-muted)]">entityId:</span>
            <code className="font-mono text-emerald-400">{sessionInfo.entityId}</code>
          </div>
        )}
        {sdkMode === 'dummy' && sdkStatus === 'idle' && <p className="text-[10px] text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded px-2 py-1">dummy mode — SDK uses built-in test data, no credentials required.</p>}
        {sdkMode === 'development' && !hasCreds && <p className="text-[10px] text-amber-400">Add credentials above to use development mode.</p>}
      </Card>

      {/* Step 2 — SDK container + event log */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 2 — SDK Verification</p>
      </Card>
      <div className="flex gap-3 items-stretch">
        <div className="flex-1 min-w-0 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] overflow-hidden">
          <div className="px-3 py-2 border-b border-[var(--border-color)] bg-[var(--bg-tertiary)]">
            <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">SDK Container</span>
          </div>
          <div className="relative" style={{ minHeight: 400 }}>
            <div id="onesdk-container" className="w-full" style={{ minHeight: 400 }} />
          </div>
        </div>
        <div className="w-72 shrink-0 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-color)] bg-[var(--bg-tertiary)] rounded-t-lg shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-[var(--text-secondary)]">SDK Events</span>
              {events.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 text-[9px] font-mono">{events.length}</span>}
            </div>
            {events.length > 0 && <button onClick={() => setEvents([])} className="text-[10px] text-[var(--text-muted)] hover:text-red-400">Clear</button>}
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-[var(--border-color)]" style={{ maxHeight: 500 }}>
            {events.length === 0 ? (
              <p className="text-[10px] text-[var(--text-muted)] p-3 italic">No events yet — click Start SDK</p>
            ) : events.map((ev, i) => (
              <div key={i} className="px-3 py-2">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="font-mono text-[9px] text-[var(--text-muted)]">{ev.ts}</span>
                  <span className="px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 text-[9px] font-mono truncate">{ev.eventName}</span>
                </div>
                {ev.args !== undefined && <pre className="text-[9px] text-[var(--text-secondary)] font-mono overflow-auto max-h-20">{JSON.stringify(ev.args, null, 2)}</pre>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Step 3 — Get Result */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 3 — Get Result</p>
        <p className="text-[10px] text-[var(--text-muted)]">After verification completes, fetch the individual entity for the full workflowResult.</p>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">GET {apiBase}/individuals/{sessionInfo?.entityId || '{entityId}'}</p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={getResult} disabled={resultLoading || !sessionInfo?.entityId}>
            {resultLoading ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}{resultLoading ? 'Loading…' : 'Get Result'}
          </Button>
          {!sessionInfo?.entityId && <span className="text-[10px] text-[var(--text-muted)] italic">Start SDK first to get entityId</span>}
        </div>
        <CurlToggle method="GET" url={`${apiBase}/individuals/${sessionInfo?.entityId || '{entityId}'}`} creds={creds} />
        {workflowResult ? (
          <KycResultSummary wr={workflowResult} fullResponse={fullResult} />
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] italic">No results yet — complete verification and click Get Result</p>
        )}
      </Card>

      <InlineWebhookPanel entityId={sessionInfo?.entityId ?? null} customerId={creds.customerId ?? ''} />
    </div>
  )
}

// ── Test Data Tab ─────────────────────────────────────────────────────────────

interface TestPersonaDoc {
  type: string
  label: string
  number: string
  secondary?: string
  state?: string
  country: string
  expiry?: string
  subtype?: string
  isPlaceholder?: boolean
}

interface TestPersona {
  id: string
  category: 'individual' | 'entity'
  name: string
  dob?: string
  address?: string
  scenario: string
  tags: string[]
  expectedResult: 'PASS' | 'FAIL' | 'REVIEW'
  docs: TestPersonaDoc[]
  notes?: string
  kycScenarioIdx?: number
}

const TEST_PERSONAS: TestPersona[] = [
  { id: 'testone-dl', category: 'individual', name: 'JAMES A TESTONE', dob: '1950-01-01', address: 'U 1/35 Conn Street, Ferntree Gully VIC 3156', scenario: 'KYC PASS', tags: ['KYC', 'PASS'], expectedResult: 'PASS', kycScenarioIdx: 0,
    docs: [{ type: 'DRIVERS_LICENSE', label: "Driver's License", number: '283229690', secondary: 'P5403241', state: 'VIC', country: 'AUS' }] },
  { id: 'testthirteen-pp', category: 'individual', name: 'PETER TESTTHIRTEEN', dob: '1962-01-01', address: '19 Libanangu Road, Kalkarindji NT 0852', scenario: 'KYC PASS', tags: ['KYC', 'PASS'], expectedResult: 'PASS', kycScenarioIdx: 2,
    docs: [{ type: 'PASSPORT', label: 'Passport', number: 'P77864526', country: 'AUS' }] },
  { id: 'testfour-medicare', category: 'individual', name: 'MARY TESTFOUR', dob: '1953-01-01', address: '17 Wattle Avenue, Warwick QLD 4370', scenario: 'KYC PASS — Medicare', tags: ['KYC', 'PASS', 'Medicare'], expectedResult: 'PASS',
    docs: [{ type: 'NATIONAL_HEALTH_ID', label: 'Medicare Card', number: '2980366171', secondary: '2', state: 'SA', country: 'AUS', subtype: 'B', expiry: '2030-01-01' }],
    notes: 'Medicare card (Green/Blue). Card colour B, individual reference 2.' },
  { id: 'testeight', category: 'individual', name: 'JENNY TESTEIGHT', dob: '1957-01-01', address: '56 Victoria Street, Fingal TAS 7214', scenario: 'KYC FAIL', tags: ['KYC', 'FAIL'], expectedResult: 'FAIL', kycScenarioIdx: 1,
    docs: [{ type: 'DRIVERS_LICENSE', label: "Driver's License", number: 'T56543', secondary: 'T03989853', state: 'TAS', country: 'AUS' }],
    notes: 'No records found in any source. No middle name.' },
  { id: 'testpartial', category: 'individual', name: 'TBD TESTPARTIAL', scenario: 'KYC REVIEW — partial match', tags: ['KYC', 'REVIEW'], expectedResult: 'REVIEW',
    docs: [{ type: 'PASSPORT', label: 'Passport', number: 'PLACEHOLDER', country: 'AUS', isPlaceholder: true }],
    notes: 'Placeholder — FrankieOne partial match testbed persona TBD.' },
  { id: 'testeleven-pep', category: 'individual', name: 'JAMES G TESTELEVEN', dob: '1960-01-01', address: '23 Isa Street, Fyshwick ACT 2609', scenario: 'AML PEP hit', tags: ['AML', 'PEP', 'REVIEW'], expectedResult: 'REVIEW', kycScenarioIdx: 3,
    docs: [{ type: 'PASSPORT', label: 'Passport', number: 'E55173628', country: 'AUS', expiry: '2030-01-01' }],
    notes: 'Middle name "G" required — omitting returns CLEAR.' },
  { id: 'testnineteen-sanction', category: 'individual', name: 'BRUCE TESTNINETEEN', dob: '1968-01-01', address: '12 Throssell Street, Curtin ACT 2605', scenario: 'AML Sanctions hit', tags: ['AML', 'Sanctions', 'REVIEW'], expectedResult: 'REVIEW', kycScenarioIdx: 4,
    docs: [{ type: 'PASSPORT', label: 'Passport', number: 'P32516383', country: 'AUS' }] },
  { id: 'testtwenty-pep-sanction', category: 'individual', name: 'STACY K TESTTWENTY', dob: '1969-01-01', address: '27 Milman Street, Marlborough QLD 4705', scenario: 'AML PEP + Sanctions', tags: ['AML', 'PEP', 'Sanctions', 'REVIEW'], expectedResult: 'REVIEW', kycScenarioIdx: 5,
    docs: [{ type: 'PASSPORT', label: 'Passport', number: 'P19031137', country: 'AUS' }],
    notes: 'Middle name "K" required.' },
  { id: 'nicholson', category: 'individual', name: 'DIANE NICHOLSON', scenario: 'AML PEP + Sanctions (realistic)', tags: ['AML', 'PEP', 'Sanctions', 'REVIEW'], expectedResult: 'REVIEW',
    docs: [{ type: 'PASSPORT', label: 'Passport', number: 'PLACEHOLDER', country: 'AUS', isPlaceholder: true }],
    notes: 'Realistic name — good for demos. Full testbed payload TBD.' },
  { id: 'testmedia', category: 'individual', name: 'TBD TESTMEDIA', scenario: 'AML Adverse Media', tags: ['AML', 'Adverse Media', 'REVIEW'], expectedResult: 'REVIEW',
    docs: [{ type: 'PASSPORT', label: 'Passport', number: 'PLACEHOLDER', country: 'AUS', isPlaceholder: true }],
    notes: 'Placeholder — FrankieOne adverse media testbed persona TBD.' },
  { id: 'fox', category: 'entity', name: 'FOX PTY. LTD.', scenario: 'KYB — Fast (~30s)', tags: ['KYB', 'Fast'], expectedResult: 'PASS',
    docs: [{ type: 'ACN', label: 'ACN', number: '052 121 347', country: 'AUS' }] },
  { id: 'bcp', category: 'entity', name: 'BCP (PITT STREET) PTY LTD', address: '1 Pitt Street, Sydney NSW 2000', scenario: 'KYB — Medium (~1-2 min)', tags: ['KYB', 'Medium'], expectedResult: 'PASS',
    docs: [{ type: 'ACN', label: 'ACN', number: '650 193 865', country: 'AUS' }] },
  { id: 'claro', category: 'entity', name: 'CLARO DISABILITY SERVICES PTY LTD', scenario: 'KYB — Complex (~5 min)', tags: ['KYB', 'Complex UBO'], expectedResult: 'PASS',
    docs: [{ type: 'ACN', label: 'ACN', number: '082 554 630', country: 'AUS' }] },
  { id: 'beacon', category: 'entity', name: 'BEACON LIGHTING GROUP LIMITED', scenario: 'KYB — ASIC Profile + Complex UBO', tags: ['KYB', 'ASIC Profile', 'Complex UBO'], expectedResult: 'PASS',
    docs: [{ type: 'ACN', label: 'ACN', number: '164 122 785', country: 'AUS' }, { type: 'ABN', label: 'ABN', number: '90 164 122 785', country: 'AUS' }] },
]

function DocCard({ doc }: { doc: TestPersonaDoc }) {
  const gradient: Record<string, string> = {
    DRIVERS_LICENSE: 'from-blue-900/40 to-blue-800/20 border-blue-700/40',
    PASSPORT: 'from-red-900/40 to-red-800/20 border-red-700/40',
    MEDICARE_CARD: 'from-green-900/40 to-green-800/20 border-green-700/40',
    ACN: 'from-slate-800/40 to-slate-700/20 border-slate-600/40',
    ABN: 'from-slate-800/40 to-slate-700/20 border-slate-600/40',
  }
  const color = gradient[doc.type] ?? 'from-gray-800/40 to-gray-700/20 border-gray-600/40'
  return (
    <div className={`bg-gradient-to-br ${color} border rounded-lg p-3 min-w-[170px] max-w-[210px] flex-shrink-0`}>
      <p className="text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-widest mb-2">{doc.label}</p>
      {doc.isPlaceholder ? (
        <div className="border border-dashed border-[var(--border-color)] rounded p-3 text-center space-y-1">
          <p className="text-[9px] text-[var(--text-muted)] italic">Image TBD</p>
          <p className="text-[10px] text-[var(--text-muted)] font-mono">—</p>
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-[12px] text-[var(--text-primary)] font-mono font-semibold tracking-wide">{doc.number}</p>
          {doc.secondary && <p className="text-[10px] text-[var(--text-muted)] font-mono">Ref: {doc.secondary}</p>}
          {doc.state && <p className="text-[10px] text-[var(--text-muted)]">State: {doc.state}</p>}
          {doc.expiry && <p className="text-[10px] text-[var(--text-muted)]">Exp: {doc.expiry}</p>}
          <p className="text-[10px] text-[var(--text-muted)]">{doc.country}</p>
        </div>
      )}
    </div>
  )
}

export function TestDataTab() {
  const [expanded, setExpanded] = useState<string | null>(null)
  const individuals = TEST_PERSONAS.filter(p => p.category === 'individual')
  const entities = TEST_PERSONAS.filter(p => p.category === 'entity')

  function PersonaRow({ p }: { p: TestPersona }) {
    const open = expanded === p.id
    const TAG_COLORS: Record<string, string> = {
      KYC: 'bg-indigo-500/10 text-indigo-400', KYB: 'bg-emerald-500/10 text-emerald-400',
      AML: 'bg-rose-500/10 text-rose-400', PEP: 'bg-amber-500/10 text-amber-400',
      Sanctions: 'bg-orange-500/10 text-orange-400', PASS: 'bg-green-500/10 text-green-400',
      FAIL: 'bg-red-500/10 text-red-400', REVIEW: 'bg-amber-500/10 text-amber-400',
      Fast: 'bg-sky-500/10 text-sky-400', Medium: 'bg-blue-500/10 text-blue-400',
    }
    return (
      <>
        <tr onClick={() => setExpanded(open ? null : p.id)}
          className={`cursor-pointer border-t border-[var(--border-color)] transition-colors ${open ? 'bg-[var(--bg-tertiary)]' : 'hover:bg-[var(--bg-tertiary)]'}`}>
          <td className="px-3 py-2.5 w-4">
            {open ? <ChevronDown size={12} className="text-[var(--text-muted)]" /> : <ChevronRight size={12} className="text-[var(--text-muted)]" />}
          </td>
          <td className="px-2 py-2.5">
            <p className="text-xs font-medium text-[var(--text-primary)]">{p.name}</p>
            {p.dob && p.dob !== 'Unknown' && <p className="text-[10px] text-[var(--text-muted)]">DOB: {p.dob}</p>}
          </td>
          <td className="px-2 py-2.5 text-[11px] text-[var(--text-secondary)] hidden md:table-cell">{p.scenario}</td>
          <td className="px-2 py-2.5"><StatusBadge status={p.expectedResult} /></td>
          <td className="px-2 py-2.5">
            <div className="flex gap-1 flex-wrap">
              {p.tags.filter(t => !['PASS','FAIL','REVIEW'].includes(t)).slice(0, 3).map(t => (
                <span key={t} className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${TAG_COLORS[t] ?? 'bg-[var(--bg-primary)] text-[var(--text-muted)]'}`}>{t}</span>
              ))}
            </div>
          </td>
        </tr>
        {open && (
          <tr className="border-t border-[var(--border-color)]">
            <td colSpan={5} className="p-4 bg-[var(--bg-primary)]">
              <div className="flex gap-3 flex-wrap">
                {p.docs.map((doc, i) => <DocCard key={i} doc={doc} />)}
              </div>
              <div className="mt-3 space-y-1">
                {p.address && <p className="text-[10px] text-[var(--text-muted)]"><span className="text-[var(--text-secondary)] font-semibold">Address:</span> {p.address}</p>}
                {p.notes && <p className="text-[10px] text-[var(--text-muted)] italic">{p.notes}</p>}
                {p.kycScenarioIdx !== undefined && (
                  <p className="text-[10px] text-sky-400 mt-1">→ Select <span className="font-mono">"{KYC_SCENARIOS[p.kycScenarioIdx].label}"</span> in the KYC tab to autofill form</p>
                )}
              </div>
            </td>
          </tr>
        )}
      </>
    )
  }

  function Section({ label, color, icon: Icon, data }: { label: string; color: string; icon: React.ElementType; data: TestPersona[] }) {
    return (
      <Card className="overflow-hidden">
        <div className={`flex items-center gap-2 px-3 py-2 border-b border-[var(--border-color)]`}>
          <Icon size={14} className={color} />
          <span className={`text-xs font-semibold ${color}`}>{label}</span>
          <span className="ml-auto text-[10px] text-[var(--text-muted)]">{data.length} records</span>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--bg-primary)]">
              <th className="w-6" />
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Name</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide hidden md:table-cell">Scenario</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Result</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Tags</th>
            </tr>
          </thead>
          <tbody>
            {data.map(p => <PersonaRow key={p.id} p={p} />)}
          </tbody>
        </table>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Section label="Individuals" color="text-indigo-400" icon={Shield} data={individuals} />
      <Section label="Organizations" color="text-emerald-400" icon={Building2} data={entities} />
    </div>
  )
}

// ── KYC V1 Tab ────────────────────────────────────────────────────────────────

const V1_BASE = 'https://api.kycaml.uat.frankiefinancial.io/compliance/v1.2'
const V1_KYB_BASE = 'https://api.uat.frankie.one/compliance/v1.2'

interface V1KycForm {
  givenName: string; middleName: string; familyName: string
  dob: string
  gender: string
  streetNumber: string; streetName: string; town: string; state: string; postalCode: string; country: string
  docType: string; docNumber: string; docExpiry: string; docCountry: string
  docRegion: string      // DL: state (VIC/NSW/etc)
  docCardNumber: string  // DL: card number → extraData document_number
  docSubType: string     // Medicare: G/B/Y
  docReference: string   // Medicare: reference 1-5
  includeDoc: boolean
  entityProfile: string
}

const BLANK_V1_KYC_FORM: V1KycForm = {
  givenName: '', middleName: '', familyName: '', dob: '', gender: 'M',
  streetNumber: '1', streetName: 'Test Street', town: 'SYDNEY', state: 'NSW', postalCode: '2000', country: 'AUS',
  docType: 'PASSPORT', docNumber: '', docExpiry: '', docCountry: 'AUS',
  docRegion: '', docCardNumber: '', docSubType: 'G', docReference: '1',
  includeDoc: false, entityProfile: 'safe_harbour',
}

interface KycV1Scenario { label: string; expectedResult: 'PASS' | 'FAIL' | 'REVIEW'; form: Partial<V1KycForm> }

const KYC_V1_SCENARIOS: KycV1Scenario[] = [
  { label: "PASS — JAMES A TESTONE (Driver's License)", expectedResult: 'PASS', form: { givenName: 'JAMES', middleName: 'A', familyName: 'TESTONE', dob: '1950-01-01', gender: 'M', streetNumber: '35', streetName: 'CONN STREET', town: 'FERNTREE GULLY', state: 'VIC', postalCode: '3156', country: 'AUS', docType: 'DRIVERS_LICENCE', docNumber: '283229690', docExpiry: '', docCountry: 'AUS', docRegion: 'VIC', docCardNumber: 'P5403241' } },
  { label: "FAIL — JENNY TESTEIGHT (Driver's License)", expectedResult: 'FAIL', form: { givenName: 'JENNY', middleName: '', familyName: 'TESTEIGHT', dob: '1957-01-01', gender: 'F', streetNumber: '56', streetName: 'VICTORIA STREET', town: 'FINGAL', state: 'TAS', postalCode: '7214', country: 'AUS', docType: 'DRIVERS_LICENCE', docNumber: 'T56543', docExpiry: '', docCountry: 'AUS', docRegion: 'TAS', docCardNumber: 'T03989853' } },
  { label: 'PASS — PETER TESTTHIRTEEN (Passport)', expectedResult: 'PASS', form: { givenName: 'PETER', middleName: '', familyName: 'TESTTHIRTEEN', dob: '1962-01-01', gender: 'M', streetNumber: '19', streetName: 'LIBANANGU ROAD', town: 'KALKARINDJI', state: 'NT', postalCode: '0852', country: 'AUS', includeDoc: true, docType: 'PASSPORT', docNumber: 'P77864526', docExpiry: '', docCountry: 'AUS' } },
  { label: 'REVIEW — PEP — JAMES G TESTELEVEN (Passport)', expectedResult: 'REVIEW', form: { givenName: 'JAMES', middleName: 'G', familyName: 'TESTELEVEN', dob: '1960-01-01', gender: 'M', streetNumber: '23', streetName: 'ISA STREET', town: 'FYSHWICK', state: 'ACT', postalCode: '2609', country: 'AUS', includeDoc: true, docType: 'PASSPORT', docNumber: 'E55173628', docExpiry: '', docCountry: 'AUS' } },
  { label: 'REVIEW — Sanction — BRUCE TESTNINETEEN (Passport)', expectedResult: 'REVIEW', form: { givenName: 'BRUCE', middleName: '', familyName: 'TESTNINETEEN', dob: '1968-01-01', gender: 'M', streetNumber: '12', streetName: 'THROSSELL STREET', town: 'CURTIN', state: 'ACT', postalCode: '2605', country: 'AUS', includeDoc: true, docType: 'PASSPORT', docNumber: 'P32516383', docExpiry: '', docCountry: 'AUS' } },
  { label: 'REVIEW — PEP+Sanction — STACY K TESTTWENTY (Passport)', expectedResult: 'REVIEW', form: { givenName: 'STACY', middleName: 'K', familyName: 'TESTTWENTY', dob: '1969-01-01', gender: 'F', streetNumber: '27', streetName: 'MILMAN STREET', town: 'MARLBOROUGH', state: 'QLD', postalCode: '4705', country: 'AUS', includeDoc: true, docType: 'PASSPORT', docNumber: 'P19031137', docExpiry: '', docCountry: 'AUS' } },
  { label: 'PASS — MARY TESTFOUR (Medicare)', expectedResult: 'PASS', form: { givenName: 'MARY', middleName: '', familyName: 'TESTFOUR', dob: '1953-01-01', gender: 'F', streetNumber: '17', streetName: 'WATTLE AVENUE', town: 'WARWICK', state: 'QLD', postalCode: '4370', country: 'AUS', includeDoc: true, docType: 'NATIONAL_HEALTH_ID', docNumber: '2980366171', docExpiry: '2030-01-01', docCountry: 'AUS', docSubType: 'B', docReference: '2' } },
]

function buildV1KycPayload(form: V1KycForm) {
  const entity: Record<string, unknown> = {
    entityProfile: form.entityProfile,
    entityType: 'INDIVIDUAL',
    name: { givenName: form.givenName, ...(form.middleName.trim() ? { middleName: form.middleName.trim() } : {}), familyName: form.familyName },
    dateOfBirth: { dateOfBirth: form.dob },
    gender: form.gender,
    addresses: [{ addressType: 'RESIDENTIAL1', streetNumber: form.streetNumber, streetName: form.streetName, town: form.town, state: form.state, postalCode: form.postalCode, country: form.country }],
    extraData: [
      { kvpKey: 'consent.general', kvpValue: 'true', kvpType: 'general.bool' },
      { kvpKey: 'consent.docs', kvpValue: 'true', kvpType: 'general.bool' },
      { kvpKey: 'consent.creditheader', kvpValue: 'true', kvpType: 'general.bool' },
    ],
  }
  if (form.includeDoc && form.docNumber) {
    const doc: Record<string, unknown> = { idType: form.docType, idNumber: form.docNumber, country: form.docCountry }
    if (form.docExpiry) doc.idExpiry = form.docExpiry
    if (form.docType === 'DRIVERS_LICENCE') {
      if (form.docRegion) doc.region = form.docRegion
      if (form.docCardNumber) doc.extraData = [{ kvpKey: 'document_number', kvpType: 'general.string', kvpValue: form.docCardNumber }]
    }
    if (form.docType === 'NATIONAL_HEALTH_ID') {
      if (form.docSubType) doc.idSubType = form.docSubType
      doc.extraData = [{ kvpKey: 'reference', kvpValue: form.docReference || '1' }]
    }
    entity.identityDocs = [doc]
  }
  return { entity }
}

export function KycV1Tab({ creds, addLog }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const v1Creds = { ...creds, apiBase: V1_BASE }
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [form, setForm] = useState<V1KycForm>(BLANK_V1_KYC_FORM)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [result, setResult] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)

  const payload = buildV1KycPayload(form)

  async function doFetch(method: string, path: string, body: unknown | undefined, annotation: string) {
    const url = `${V1_BASE}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(v1Creds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }

  async function createEntity() {
    setLoading('create'); setResult(null); setFullResult(null)
    try {
      const data = await doFetch('POST', '/entity/new/verify/profile/simple', payload, 'V1: Create + verify individual (synchronous — result in response)')
      setResult(data); setFullResult(data)
      const eid = (data as { entity?: { entityId?: string } })?.entity?.entityId
      if (eid) setEntityId(eid)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function getEntity() {
    if (!entityId) return
    setLoading('get')
    try {
      const data = await doFetch('GET', `/entity/${entityId}`, undefined, 'V1: Re-fetch entity result')
      setResult(data); setFullResult(data)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  function reset() { setEntityId(null); setResult(null); setFullResult(null) }

  const inputCls = 'w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs font-mono text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-primary'

  return (
    <div className="space-y-3">

      {/* Step 1 — Choose Recipe */}
      <Card className="p-3 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 1 — Entity Recipe</p>
        <p className="text-[10px] text-[var(--text-muted)]">Select the recipe (entityProfile) that defines which checks run. This determines the verification flow.</p>
        <select className={inputCls} value={form.entityProfile} onChange={e => setForm(f => ({ ...f, entityProfile: e.target.value }))}>
          {RECIPES.filter(r => r.key !== 'organisation').map(r => <option key={r.key} value={r.key}>{r.name} — {r.key}</option>)}
        </select>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">entityProfile: <span className="text-amber-400">{form.entityProfile}</span></p>
      </Card>

      {/* Step 2 — Individual Data */}
      <Card className="p-3 space-y-3">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 2 — Individual Data</p>

        <div className="flex items-center gap-2 flex-wrap">
          <select value={scenarioIdx ?? ''} onChange={e => {
            const v = e.target.value
            if (v === '') { setScenarioIdx(null); setForm(BLANK_V1_KYC_FORM) }
            else { const idx = parseInt(v); setScenarioIdx(idx); setForm({ ...BLANK_V1_KYC_FORM, ...KYC_V1_SCENARIOS[idx].form, entityProfile: form.entityProfile }) }
          }} className="flex-1 min-w-0 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">— Custom / manual entry —</option>
            {KYC_V1_SCENARIOS.map((s, i) => <option key={i} value={i}>{s.label}</option>)}
          </select>
          {scenarioIdx !== null && <span className={`text-[10px] font-bold shrink-0 ${resultColor(KYC_V1_SCENARIOS[scenarioIdx].expectedResult)}`}>Expected: {KYC_V1_SCENARIOS[scenarioIdx].expectedResult}</span>}
        </div>

        <div className="space-y-3">
          <div className="space-y-2">
            <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Personal Details</p>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">Given Name</label><input className={inputCls} value={form.givenName} onChange={e => setForm(f => ({ ...f, givenName: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Middle Name</label><input className={inputCls} value={form.middleName} onChange={e => setForm(f => ({ ...f, middleName: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Family Name</label><input className={inputCls} value={form.familyName} onChange={e => setForm(f => ({ ...f, familyName: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">Date of Birth</label><input className={inputCls} placeholder="YYYY-MM-DD" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Gender</label>
                <select className={inputCls} value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                  {['M','F','U'].map(g => <option key={g}>{g}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-[var(--border-color)] pt-2 space-y-2">
            <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Address</p>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">Street Number</label><input className={inputCls} value={form.streetNumber} onChange={e => setForm(f => ({ ...f, streetNumber: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Street Name</label><input className={inputCls} value={form.streetName} onChange={e => setForm(f => ({ ...f, streetName: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div><label className="text-[10px] text-[var(--text-muted)]">Town</label><input className={inputCls} value={form.town} onChange={e => setForm(f => ({ ...f, town: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">State</label><input className={inputCls} value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Postcode</label><input className={inputCls} value={form.postalCode} onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))} /></div>
              <div><label className="text-[10px] text-[var(--text-muted)]">Country</label><input className={inputCls} value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} /></div>
            </div>
          </div>

          <div className="border-t border-[var(--border-color)] pt-2 space-y-2">
            <label className="flex items-center gap-2 text-[10px] text-[var(--text-secondary)] cursor-pointer">
              <input type="checkbox" checked={form.includeDoc} onChange={e => setForm(f => ({ ...f, includeDoc: e.target.checked }))} className="rounded" />
              Include identity document
            </label>
            {form.includeDoc && (
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px] text-[var(--text-muted)]">Doc Type</label>
                  <select className={inputCls} value={form.docType} onChange={e => setForm(f => ({ ...f, docType: e.target.value }))}>
                    {['PASSPORT','DRIVERS_LICENCE','NATIONAL_HEALTH_ID','NATIONAL_ID'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className="text-[10px] text-[var(--text-muted)]">{form.docType === 'NATIONAL_HEALTH_ID' ? 'Card Number' : 'Doc Number'}</label><input className={inputCls} value={form.docNumber} onChange={e => setForm(f => ({ ...f, docNumber: e.target.value }))} /></div>
                {form.docType !== 'NATIONAL_HEALTH_ID' && (
                  <div><label className="text-[10px] text-[var(--text-muted)]">Expiry</label><input className={inputCls} placeholder="YYYY-MM-DD" value={form.docExpiry} onChange={e => setForm(f => ({ ...f, docExpiry: e.target.value }))} /></div>
                )}
                <div><label className="text-[10px] text-[var(--text-muted)]">Country</label><input className={inputCls} value={form.docCountry} onChange={e => setForm(f => ({ ...f, docCountry: e.target.value }))} /></div>
                {form.docType === 'DRIVERS_LICENCE' && (<>
                  <div><label className="text-[10px] text-[var(--text-muted)]">State (region)</label>
                    <select className={inputCls} value={form.docRegion} onChange={e => setForm(f => ({ ...f, docRegion: e.target.value }))}>
                      <option value="">— select —</option>
                      {['VIC','NSW','QLD','WA','SA','TAS','ACT','NT'].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><label className="text-[10px] text-[var(--text-muted)]">Card Number</label><input className={inputCls} placeholder="document_number" value={form.docCardNumber} onChange={e => setForm(f => ({ ...f, docCardNumber: e.target.value }))} /></div>
                </>)}
                {form.docType === 'NATIONAL_HEALTH_ID' && (<>
                  <div><label className="text-[10px] text-[var(--text-muted)]">Card Colour</label>
                    <select className={inputCls} value={form.docSubType} onChange={e => setForm(f => ({ ...f, docSubType: e.target.value }))}>
                      <option value="G">G — Green</option>
                      <option value="B">B — Blue</option>
                      <option value="Y">Y — Yellow</option>
                    </select>
                  </div>
                  <div><label className="text-[10px] text-[var(--text-muted)]">Expiry</label><input className={inputCls} placeholder="YYYY-MM-DD" value={form.docExpiry} onChange={e => setForm(f => ({ ...f, docExpiry: e.target.value }))} /></div>
                  <div><label className="text-[10px] text-[var(--text-muted)]">Reference (1–5)</label><input className={inputCls} placeholder="1" value={form.docReference} onChange={e => setForm(f => ({ ...f, docReference: e.target.value }))} /></div>
                </>)}
              </div>
            )}
          </div>
        </div>

        <CurlToggle method="POST" url={`${V1_BASE}/entity/new/verify/profile/simple`} creds={creds} body={payload} />

        <div className="space-y-1 pt-1">
          <p className="text-[9px] text-[var(--text-muted)] font-mono">POST /entity/new/verify/profile/simple</p>
          <Button variant="primary" size="sm" onClick={createEntity} disabled={loading !== null || !hasCreds}>
            {loading === 'create' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
            {loading === 'create' ? 'Creating…' : 'Create + Verify'}
          </Button>
          {!hasCreds && <p className="text-[10px] text-amber-400">Add credentials in the credentials panel.</p>}
          {entityId && (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-[10px] mt-2">
              <span className="text-cyan-400 font-semibold">Created</span>
              <span className="text-[var(--text-muted)]">entityId:</span>
              <code className="font-mono text-cyan-400 break-all">{entityId}</code>
              <button onClick={reset} className="ml-auto text-[var(--text-muted)] hover:text-red-400"><X size={10} /></button>
            </div>
          )}
        </div>
      </Card>

      {/* Step 3 — Result */}
      <Card className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 3 — Result</p>
          {result && <button onClick={reset} className="text-[10px] text-[var(--text-muted)] hover:text-red-400">Reset</button>}
        </div>
        {result ? (
          <V1ResultSummary data={result} fullResponse={fullResult} expectedResult={scenarioIdx !== null ? KYC_V1_SCENARIOS[scenarioIdx].expectedResult : undefined} />
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] italic">No results yet — click Create + Verify above.</p>
        )}
      </Card>

      {/* Step 4 — Re-fetch (optional) */}
      <Card className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 4 — Re-fetch Entity <span className="font-normal text-[var(--text-muted)]">(optional)</span></p>
          {result && <button onClick={reset} className="text-[10px] text-[var(--text-muted)] hover:text-red-400">Reset</button>}
        </div>
        <p className="text-[9px] text-[var(--text-muted)]">V1 KYC is synchronous — the result is returned directly from Step 1. Use this to re-fetch the entity if needed.</p>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">GET /entity/{entityId || '{entityId}'}</p>
        <Button variant="secondary" size="sm" onClick={getEntity} disabled={loading !== null || !hasCreds || !entityId}>
          {loading === 'get' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
          {loading === 'get' ? 'Loading…' : 'Get Entity'}
        </Button>
        <CurlToggle method="GET" url={`${V1_BASE}/entity/${entityId || '{entityId}'}`} creds={creds} />
      </Card>

      <InlineWebhookPanel entityId={entityId} customerId={creds.customerId ?? ''} />
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function kvpMap(notes: any[]): Record<string, string> {
  if (!Array.isArray(notes)) return {}
  const m: Record<string, string> = {}
  for (const n of notes) m[n.kvpKey] = n.kvpValue
  return m
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function V1ResultSummary({ data, fullResponse, expectedResult }: { data: any; fullResponse: unknown; expectedResult?: string }) {
  const [rawOpen, setRawOpen] = useState(false)
  const epr = data?.entityProfileResult
  const entityId = data?.entity?.entityId ?? data?.entityId

  // Parse risk breakdown from checkRisk
  const cr = data?.checkRisk
  const crNotes = kvpMap(cr?.resultNotes ?? [])
  const riskScore = cr?.riskLevel  // numeric 0-100
  const riskLevel = crNotes['Risk.Level']
  const riskBracket = crNotes['Risk.Bracket']
  const confidenceLevel = crNotes['Confidence.Level']

  // Risk factors: risk_factor.X.value + risk_factor.X.score
  const riskFactors: { name: string; value: string; score: string }[] = []
  for (const key of Object.keys(crNotes)) {
    const m = key.match(/^risk_factor\.(.+)\.value$/)
    if (m) {
      const factor = m[1]
      riskFactors.push({ name: factor, value: crNotes[key], score: crNotes[`risk_factor.${factor}.score`] ?? '' })
    }
  }

  // Match counts from checkSummary
  const cs = data?.checkSummary
  const csNotes = kvpMap(cs?.resultNotes ?? [])
  const matchCounts = [
    { label: 'Name', key: 'Info.NameMatchCount' },
    { label: 'Address', key: 'Info.AddressMatchCount' },
    { label: 'DoB', key: 'Info.DoBMatchCount' },
    { label: 'Gov ID', key: 'Info.GovIDMatchCount' },
    { label: 'Other ID', key: 'Info.OtherIDMatchCount' },
  ].filter(m => csNotes[m.key] != null)

  // Issues from epr.issueList
  const issueList: string[] = epr?.issueList ?? []

  const BADGE_RISK: Record<string, string> = {
    UNACCEPTABLE: 'bg-red-500/15 text-red-400 border-red-500/30',
    HIGH: 'bg-red-500/15 text-red-400 border-red-500/30',
    MEDIUM: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    LOW: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  }

  function factorLabel(raw: string) {
    return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  function factorValueLabel(factor: string, val: string) {
    if (factor === 'confidence') return val  // numeric
    return val === '1' || val === 'true' ? 'Yes' : val === '0' || val === 'false' ? 'No' : val
  }

  return (
    <div className="space-y-2 mt-2">

      {/* Outcome */}
      {epr && (
        <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Outcome</p>
            {expectedResult && <span className={`text-[10px] font-bold ${resultColor(expectedResult)}`}>Expected: {expectedResult}</span>}
          </div>

          {/* Main result row */}
          <div className="flex items-center gap-3 flex-wrap">
            {epr.actionRecommended && <StatusBadge status={epr.actionRecommended} />}
            {riskLevel && <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border ${BADGE_RISK[riskLevel] ?? 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>{riskLevel}</span>}
            {issueList.length > 0 && issueList.map(issue => (
              <span key={issue} className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border bg-red-500/10 text-red-400 border-red-500/20">{issue}</span>
            ))}
          </div>

          {/* Risk score bar */}
          {riskScore != null && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-[var(--text-muted)] font-mono">Risk Score</span>
                <span className={`font-bold font-mono text-[11px] ${riskScore >= 80 ? 'text-red-400' : riskScore >= 50 ? 'text-amber-400' : 'text-emerald-400'}`}>{riskScore} / 100</span>
              </div>
              <div className="h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden">
                <div className={`h-full rounded-full transition-all ${riskScore >= 80 ? 'bg-red-500' : riskScore >= 50 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${riskScore}%` }} />
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-4 flex-wrap text-[10px] font-mono text-[var(--text-muted)]">
            {riskBracket && <span>Bracket: <span className="text-[var(--text-secondary)]">{riskBracket}</span></span>}
            {confidenceLevel && <span>Confidence: <span className="text-[var(--text-secondary)]">{confidenceLevel}</span></span>}
            {epr.profileName && <span>Profile: <span className="text-[var(--text-secondary)]">{epr.profileName}</span></span>}
            {entityId && <span>entityId: <span className="text-[var(--text-secondary)]">{entityId}</span></span>}
          </div>
        </div>
      )}

      {/* Check Results */}
      {epr?.checkResults?.length > 0 && (
        <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-1.5">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Check Results</p>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {epr.checkResults.map((check: any, i: number) => (
            <div key={i} className="flex items-start gap-2 text-[10px]">
              <StatusBadge status={check.result} />
              <span className="text-[var(--text-secondary)] font-medium">{check.name ?? check.checkType}</span>
              {check.message && <span className="text-[var(--text-muted)] italic truncate">{check.message}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Risk Factors */}
      {riskFactors.length > 0 && (
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide px-3 py-2 border-b border-[var(--border-color)]">Risk Factors</p>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-[var(--border-color)] bg-[var(--bg-primary)]">
                <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-muted)]">Factor</th>
                <th className="text-left px-3 py-1.5 font-semibold text-[var(--text-muted)]">Value</th>
                <th className="text-right px-3 py-1.5 font-semibold text-[var(--text-muted)]">Score</th>
              </tr>
            </thead>
            <tbody>
              {riskFactors.map((f, i) => (
                <tr key={f.name} className={i % 2 === 1 ? 'bg-[var(--bg-primary)]' : ''}>
                  <td className="px-3 py-1.5 font-mono text-[var(--text-secondary)]">{factorLabel(f.name)}</td>
                  <td className={`px-3 py-1.5 font-mono font-bold ${(f.value === '1' || f.value === 'true') && f.name !== 'confidence' ? 'text-red-400' : 'text-[var(--text-secondary)]'}`}>
                    {factorValueLabel(f.name, f.value)}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-right text-[var(--text-secondary)]">{f.score !== '' ? f.score : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Match Counts */}
      {matchCounts.length > 0 && (
        <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-2">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Match Counts</p>
          <div className="flex items-center gap-3 flex-wrap">
            {matchCounts.map(m => {
              const count = parseInt(csNotes[m.key] ?? '0')
              return (
                <div key={m.label} className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-[var(--text-muted)]">{m.label}:</span>
                  <span className={`font-bold font-mono ${count > 0 ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Raw JSON */}
      <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
        <button onClick={() => setRawOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-tertiary)] transition-colors">
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Raw JSON Response</span>
          {rawOpen ? <ChevronUp size={10} className="text-[var(--text-muted)]" /> : <ChevronDown size={10} className="text-[var(--text-muted)]" />}
        </button>
        {rawOpen && (
          <div className="border-t border-[var(--border-color)] bg-[var(--bg-primary)] p-2 max-h-64 overflow-y-auto">
            <JsonView data={fullResponse} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── KYB V1 Result Summary ────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function V1KybResultSummary({ data, fullResponse }: { data: any; fullResponse: unknown }) {
  const [rawOpen, setRawOpen] = useState(false)
  const entity = data?.entity
  const org = entity?.organisationData
  if (!entity && !data?.requestId) return null

  // Parse extraData into a map
  const extra = kvpMap(entity?.extraData ?? [])

  const abn = extra['ABN'] || extra['abr.retrieved.abn']
  const acn = extra['ACN'] || extra['abr.retrieved.acn']
  const entityStatus = extra['abr.retrieved.entity_status']
  const entityStatusFrom = extra['abr.retrieved.entity_status.effective_from']
  const gstFrom = extra['abr.retrieved.gst_from_date']
  const gstTo = extra['abr.retrieved.gst_to_date']
  const jurisdiction = extra['controllingJurisdictionExpanded'] || extra['controllingJurisdiction']

  const statusOk = org?.status?.code === 'REGD'

  const addresses = entity?.addresses ?? []
  const primaryAddr = addresses.find((a: { addressType: string }) => a.addressType === 'REGISTERED_OFFICE') ?? addresses[0]

  return (
    <div className="space-y-2 mt-2">

      {/* Organisation Summary */}
      {org && (
        <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-2">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Organisation</p>

          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border ${statusOk ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-red-500/15 text-red-400 border-red-500/30'}`}>
              {org.status?.description ?? org.normalisedLegalStatus}
            </span>
            {org.type?.description && <span className="text-[10px] text-[var(--text-muted)]">{org.type.description}</span>}
            {org.subclass?.description && <span className="text-[10px] text-[var(--text-muted)]">· {org.subclass.description}</span>}
          </div>

          <table className="w-full text-[10px]">
            <tbody>
              {[
                ['Registered Name', org.registeredName],
                ['ACN', acn],
                ['ABN', abn],
                ['Registration State', org.registration?.state],
                ['Registration Date', org.registration?.date],
                ['Jurisdiction', jurisdiction],
                ['Start Date', org.startDate],
              ].filter(([, v]) => v).map(([k, v]) => (
                <tr key={k} className="border-b border-[var(--border-color)] last:border-0">
                  <td className="py-1 pr-3 text-[var(--text-muted)] font-mono w-36 shrink-0">{k}</td>
                  <td className="py-1 text-[var(--text-secondary)] font-mono">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ABR Status */}
      {(entityStatus || gstFrom) && (
        <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-1.5">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">ABR Status</p>
          <table className="w-full text-[10px]">
            <tbody>
              {[
                ['Entity Status', entityStatus ? `${entityStatus}${entityStatusFrom ? ` (from ${entityStatusFrom})` : ''}` : null],
                ['GST Registered', gstFrom ? `${gstFrom} → ${gstTo || 'current'}` : null],
              ].filter(([, v]) => v).map(([k, v]) => (
                <tr key={k} className="border-b border-[var(--border-color)] last:border-0">
                  <td className="py-1 pr-3 text-[var(--text-muted)] font-mono w-36">{k}</td>
                  <td className={`py-1 font-mono ${k === 'Entity Status' && entityStatus === 'Cancelled' ? 'text-red-400' : 'text-[var(--text-secondary)]'}`}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Addresses */}
      {primaryAddr && (
        <div className="border border-[var(--border-color)] rounded-lg p-3 space-y-1.5">
          <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Addresses ({addresses.length})</p>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {addresses.slice(0, 4).map((a: any, i: number) => (
            <div key={i} className="flex items-start gap-2 text-[10px]">
              <span className="shrink-0 inline-flex px-1 py-0.5 rounded text-[9px] bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-muted)] font-mono">{a.addressType}</span>
              <span className="text-[var(--text-secondary)] font-mono">
                {[a.streetName, a.town, a.state, a.postalCode, a.country].filter(Boolean).join(', ') || `${a.state} ${a.postalCode}`}
                {a.startDate && a.startDate !== '0001-01-01' && <span className="text-[var(--text-muted)] ml-2">from {a.startDate}</span>}
              </span>
            </div>
          ))}
          {addresses.length > 4 && <p className="text-[9px] text-[var(--text-muted)]">+{addresses.length - 4} more addresses</p>}
        </div>
      )}

      {/* requestId */}
      {data?.requestId && (
        <div className="text-[10px] font-mono text-[var(--text-muted)]">requestId: <span className="text-[var(--text-secondary)]">{data.requestId}</span></div>
      )}

      {/* Raw JSON */}
      <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
        <button onClick={() => setRawOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-tertiary)] transition-colors">
          <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Raw JSON Response</span>
          {rawOpen ? <ChevronUp size={10} className="text-[var(--text-muted)]" /> : <ChevronDown size={10} className="text-[var(--text-muted)]" />}
        </button>
        {rawOpen && (
          <div className="border-t border-[var(--border-color)] bg-[var(--bg-primary)] p-2 max-h-64 overflow-y-auto">
            <JsonView data={fullResponse} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── KYB V1 Tab ────────────────────────────────────────────────────────────────

interface V1KybScenario {
  label: string
  orgName: string
  acn: string
  tags: string[]
}

const KYB_V1_SCENARIOS: V1KybScenario[] = [
  { label: 'FOX PTY. LTD. — Fast (~30s)', orgName: 'FOX PTY. LTD.', acn: '052121347', tags: ['Fast'] },
  { label: 'BCP (PITT STREET) PTY LTD — Medium (~1-2 min)', orgName: 'BCP (PITT STREET) PTY LTD', acn: '650193865', tags: ['Medium'] },
  { label: 'CLARO DISABILITY SERVICES PTY LTD — Complex (~5 min)', orgName: 'CLARO DISABILITY SERVICES PTY LTD', acn: '082554630', tags: ['Complex UBO'] },
  { label: 'BEACON LIGHTING GROUP LIMITED', orgName: 'BEACON LIGHTING GROUP LIMITED', acn: '164122785', tags: ['ASIC Profile', 'Complex UBO'] },
]

export function KybV1Tab({ creds, addLog }: { creds: Creds; addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void }) {
  const v1KybCreds = { ...creds, apiBase: V1_KYB_BASE }
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [acnValue, setAcnValue] = useState('')
  const [idKeyType, setIdKeyType] = useState<'ACN' | 'ABN'>('ACN')
  // Query params
  const [checkTypes, setCheckTypes] = useState<string[]>(['one_plus', 'pep_media'])
  const [entityCategories, setEntityCategories] = useState<string[]>(['organisation', 'officers_directors', 'direct_owners', 'ubos'])
  const [resultLevel, setResultLevel] = useState('full')
  const [generateReport, setGenerateReport] = useState('UBO')
  const [ownershipMode, setOwnershipMode] = useState('full')
  const [entityId, setEntityId] = useState<string | null>(null)
  const [requestId, setRequestId] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [result, setResult] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)

  const payload = {
    organisation: {
      entityType: 'ORGANISATION',
      extraData: [{ kvpKey: idKeyType, kvpValue: acnValue, kvpType: 'id.external' }],
    },
  }

  function buildQs() {
    const parts: string[] = []
    if (checkTypes.length) parts.push(`checkType=${checkTypes.join(',')}`)
    if (entityCategories.length) parts.push(`entityCategories=${entityCategories.join(',')}`)
    parts.push(`resultLevel=${resultLevel}`)
    if (generateReport) parts.push(`generateReport=${generateReport}`)
    parts.push(`ownershipMode=${ownershipMode}`)
    return parts.join('&')
  }

  async function doFetch(method: string, path: string, body: unknown | undefined, annotation: string) {
    const url = `${V1_KYB_BASE}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(v1KybCreds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }

  async function submitQuery() {
    setLoading('create'); setResult(null); setFullResult(null)
    try {
      const qs = buildQs()
      const data = await doFetch('POST', `/business/ownership/query?${qs}`, payload, 'V1: Business ownership query') as { entity?: { entityId?: string }; entityId?: string; requestId?: string }
      const eid = data?.entity?.entityId ?? data?.entityId
      const rid = data?.requestId
      if (eid) setEntityId(eid)
      if (rid) setRequestId(rid)
      // Don't show result here — Step 2 poll will populate it
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function pollResult() {
    if (!entityId) return
    setLoading('poll')
    try {
      const data = await doFetch('GET', `/entity/${entityId}`, undefined, 'V1: Poll business ownership result')
      setResult(data); setFullResult(data)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  function applyScenario(idx: number) { setAcnValue(KYB_V1_SCENARIOS[idx].acn) }
  function reset() { setEntityId(null); setRequestId(null); setResult(null); setFullResult(null) }
  function toggleCheckType(v: string) { setCheckTypes(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]) }
  function toggleCategory(v: string) { setEntityCategories(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]) }

  const inputCls = 'w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs font-mono text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-primary'
  const qs = buildQs()

  return (
    <div className="space-y-3">

      {/* Step 1 — Query */}
      <Card className="p-3 space-y-3">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 1 — Business Ownership Query</p>

        <div className="flex items-center gap-2 flex-wrap">
          <select value={scenarioIdx ?? ''} onChange={e => {
            const v = e.target.value
            if (v === '') { setScenarioIdx(null) }
            else { const idx = parseInt(v); setScenarioIdx(idx); applyScenario(idx) }
          }} className="flex-1 min-w-0 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">— Manual entry —</option>
            {KYB_V1_SCENARIOS.map((s, i) => <option key={i} value={i}>{s.label}</option>)}
          </select>
          {scenarioIdx !== null && KYB_V1_SCENARIOS[scenarioIdx].tags.map(t => (
            <span key={t} className="bg-teal-500/10 text-teal-400 px-1.5 py-0.5 rounded text-[9px]">{t}</span>
          ))}
        </div>

        {/* Organisation identifier */}
        <div className="space-y-2">
          <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Organisation</p>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="text-[10px] text-[var(--text-muted)]">ID Type</label>
              <select className={inputCls} value={idKeyType} onChange={e => setIdKeyType(e.target.value as 'ACN' | 'ABN')}>
                <option value="ACN">ACN</option>
                <option value="ABN">ABN</option>
              </select>
            </div>
            <div className="col-span-3">
              <label className="text-[10px] text-[var(--text-muted)]">Number</label>
              <input className={inputCls} value={acnValue} onChange={e => setAcnValue(e.target.value)} placeholder="052121347" />
            </div>
          </div>
        </div>

        {/* Query params */}
        <div className="border-t border-[var(--border-color)] pt-2 space-y-3">
          <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Query Parameters</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] text-[var(--text-muted)] mb-1">checkType</p>
              <div className="space-y-1">
                {[['one_plus', 'one_plus (KYC 1+1)'], ['pep_media', 'pep_media (AML)']].map(([v, label]) => (
                  <label key={v} className="flex items-center gap-1.5 text-[10px] text-[var(--text-secondary)] cursor-pointer">
                    <input type="checkbox" checked={checkTypes.includes(v)} onChange={() => toggleCheckType(v)} className="rounded" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] text-[var(--text-muted)] mb-1">entityCategories</p>
              <div className="space-y-1">
                {['organisation', 'officers_directors', 'direct_owners', 'ubos'].map(v => (
                  <label key={v} className="flex items-center gap-1.5 text-[10px] text-[var(--text-secondary)] cursor-pointer">
                    <input type="checkbox" checked={entityCategories.includes(v)} onChange={() => toggleCategory(v)} className="rounded" />
                    {v}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-[var(--text-muted)]">resultLevel</label>
              <select className={inputCls} value={resultLevel} onChange={e => setResultLevel(e.target.value)}>
                {['simple', 'summary', 'full'].map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-[var(--text-muted)]">generateReport</label>
              <select className={inputCls} value={generateReport} onChange={e => setGenerateReport(e.target.value)}>
                <option value="UBO">UBO</option>
                <option value="SINGLE-LEVEL-AML">SINGLE-LEVEL-AML</option>
                <option value="">none</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-[var(--text-muted)]">ownershipMode</label>
              <select className={inputCls} value={ownershipMode} onChange={e => setOwnershipMode(e.target.value)}>
                <option value="full">full</option>
                <option value="partial">partial</option>
                <option value="onlyProfile">onlyProfile</option>
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-1 pt-1">
          <p className="text-[9px] text-[var(--text-muted)] font-mono break-all">POST /business/ownership/query?{qs}</p>
          <Button variant="primary" size="sm" onClick={submitQuery} disabled={loading !== null || !hasCreds || !acnValue}>
            {loading === 'create' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
            {loading === 'create' ? 'Submitting…' : 'Submit Query'}
          </Button>
          <CurlToggle method="POST" url={`${V1_KYB_BASE}/business/ownership/query?${qs}`} creds={creds} body={payload} />
          {!hasCreds && <p className="text-[10px] text-amber-400">Add credentials in the credentials panel.</p>}
          {(entityId || requestId) && (
            <div className="space-y-1 mt-2">
              {entityId && (
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-teal-500/10 border border-teal-500/20 text-[10px]">
                  <span className="text-teal-400 font-semibold shrink-0">entityId</span>
                  <code className="font-mono text-teal-400 break-all">{entityId}</code>
                  <button onClick={reset} className="ml-auto text-[var(--text-muted)] hover:text-red-400 shrink-0"><X size={10} /></button>
                </div>
              )}
              {requestId && (
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[10px]">
                  <span className="text-[var(--text-muted)] font-semibold shrink-0">requestId</span>
                  <code className="font-mono text-[var(--text-secondary)] break-all">{requestId}</code>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Step 2 — Poll Result */}
      <Card className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Step 2 — Poll Result</p>
          {result && <button onClick={reset} className="text-[10px] text-[var(--text-muted)] hover:text-red-400">Reset</button>}
        </div>
        <p className="text-[9px] text-[var(--text-muted)]">Async — Step 1 returns entityId immediately. Poll until the report is ready (typically 20–300s depending on corporate structure).</p>
        <p className="text-[9px] text-[var(--text-muted)] font-mono">GET /entity/{entityId || '{entityId}'}</p>
        <Button variant="secondary" size="sm" onClick={pollResult} disabled={loading !== null || !hasCreds || !entityId}>
          {loading === 'poll' ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
          {loading === 'poll' ? 'Polling…' : 'Poll Result'}
        </Button>
        <CurlToggle method="GET" url={`${V1_KYB_BASE}/entity/${entityId || '{entityId}'}`} creds={creds} />
        {!hasCreds && <p className="text-[10px] text-amber-400">Add credentials to use this tab.</p>}
        {result ? (
          <V1KybResultSummary data={result} fullResponse={fullResult} />
        ) : (
          <p className="text-[10px] text-[var(--text-muted)] italic">No results yet — submit query first, then poll.</p>
        )}
      </Card>

      <InlineWebhookPanel entityId={entityId} customerId={creds.customerId ?? ''} />
    </div>
  )
}

// ── Tab config ────────────────────────────────────────────────────────────────

// ── Webhooks Tab ──────────────────────────────────────────────────────────────

const WEBHOOK_BASE = 'https://dealhub.pmgoncalves0.workers.dev/api/webhook/frankieone'

interface WebhookEvent {
  id: number
  receivedAt: string
  eventType: string | null
  entityId: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
  headers: Record<string, string>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jwtDecoded: any | null
}

// ── Inline Webhook Panel ──────────────────────────────────────────────────────

interface InlineWebhookPanelProps {
  entityId: string | null
  customerId: string | null
}

function InlineWebhookPanel({ entityId, customerId }: InlineWebhookPanelProps) {
  const [events, setEvents] = useState<WebhookEvent[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [lastPollAt, setLastPollAt] = useState<Date | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchEvents = useCallback(async (signal?: AbortSignal) => {
    if (!customerId) return
    try {
      const res = await fetch(`/api/webhook/frankieone/events?customerId=${encodeURIComponent(customerId ?? '')}`, signal ? { signal } : undefined)
      if (res.ok) {
        const data = (await res.json()) as WebhookEvent[]
        setEvents(data.filter(e => e.entityId === entityId))
        setLastPollAt(new Date())
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
    }
  }, [customerId, entityId])

  useEffect(() => {
    setEvents([])
    setExpandedId(null)
    if (!customerId) return
    const ac = new AbortController()
    void fetchEvents(ac.signal)
    pollingRef.current = setInterval(() => { void fetchEvents() }, 5000)
    return () => {
      ac.abort()
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [customerId, entityId, fetchEvents])

  function relativeTime(d: Date): string {
    const secs = Math.floor((Date.now() - d.getTime()) / 1000)
    if (secs < 10) return 'just now'
    if (secs < 60) return `${secs}s ago`
    return `${Math.floor(secs / 60)}m ago`
  }

  function formatTs(iso: string): string {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    } catch { return iso }
  }

  function statusBadgeClass(status: string | undefined): string {
    if (!status) return 'bg-gray-500/20 text-gray-400'
    const s = status.toUpperCase()
    if (s === 'PASS') return 'bg-green-500/20 text-green-400'
    if (s === 'FAIL') return 'bg-red-500/20 text-red-400'
    if (s === 'REFER' || s === 'REVIEW') return 'bg-orange-500/20 text-orange-400'
    if (s === 'EVENT') return 'bg-amber-500/20 text-amber-400'
    return 'bg-gray-500/20 text-gray-400'
  }

  // Panel state: no credentials
  if (!customerId) {
    return (
      <div className="mt-3 rounded border border-amber-500/20 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Radio size={11} className="text-amber-500" />
          <span className="text-[10px] font-medium text-amber-500 uppercase tracking-wide">Webhooks</span>
        </div>
        <p className="text-[10px] text-[var(--text-muted)] italic">Configure credentials to receive webhooks</p>
      </div>
    )
  }

  return (
    <div className="mt-3 rounded border border-amber-500/20">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-500/20">
        <Radio size={11} className="text-amber-500" />
        <span className="text-[10px] font-medium text-amber-500 uppercase tracking-wide flex-1">Webhooks</span>
        {events.length > 0 && (
          <span className="text-[9px] font-bold bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">
            {events.length}
          </span>
        )}
        {lastPollAt && (
          <span className="text-[9px] text-[var(--text-muted)]">{relativeTime(lastPollAt)}</span>
        )}
      </div>

      {/* Body */}
      <div className="p-2 space-y-1">
        {!entityId ? (
          <p className="text-[10px] text-[var(--text-muted)] italic py-1">Make an API call above to start listening for webhooks</p>
        ) : events.length === 0 ? (
          <div className="flex items-center gap-2 py-1">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>
            <span className="text-[10px] text-amber-500">Listening for webhooks…</span>
            <span className="text-[9px] text-[var(--text-muted)] font-mono">entity: {entityId}</span>
          </div>
        ) : (
          <>
            {[...events].sort((a, b) => b.id - a.id).map(ev => {
              const status = ev.payload?.overallStatus as string | undefined
              const isExpanded = expandedId === ev.id
              return (
                <div
                  key={ev.id}
                  className="rounded border border-amber-500/15 bg-amber-500/5 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                >
                  <div className="flex items-center gap-2 px-2 py-1">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${statusBadgeClass(status)}`}>
                      {status ?? 'EVENT'}
                    </span>
                    <span className="text-[10px] text-[var(--text-secondary)] font-mono flex-1 truncate">
                      {ev.payload?.function ?? ev.eventType ?? '—'}
                    </span>
                    <span className="text-[9px] text-[var(--text-muted)] shrink-0">{formatTs(ev.receivedAt)}</span>
                  </div>
                  {(ev.payload?.workflowName || ev.payload?.overallRiskLevel) && (
                    <div className="px-2 pb-1 text-[9px] text-[var(--text-muted)] font-mono">
                      {ev.payload?.workflowName}
                      {ev.payload?.overallRiskLevel && (
                        <span className="ml-1">• riskLevel: {ev.payload.overallRiskLevel}</span>
                      )}
                    </div>
                  )}
                  {isExpanded && (
                    <div className="border-t border-amber-500/15 p-2">
                      <pre className="text-[9px] text-[var(--text-secondary)] whitespace-pre-wrap break-all font-mono">
                        {JSON.stringify(ev.payload, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )
            })}
            {/* Listening indicator at bottom when events present */}
            <div className="flex items-center gap-2 pt-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
              </span>
              <span className="text-[9px] text-amber-500/70">Listening for webhooks…</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function WebhooksTab({ creds }: { creds: Creds }) {
  const customerId = creds.customerId?.trim() || ''
  const webhookUrl = customerId ? `${WEBHOOK_BASE}/${customerId}` : null

  const [events, setEvents] = useState<WebhookEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  async function fetchEvents() {
    if (!customerId) return
    try {
      const res = await fetch(`/api/webhook/frankieone/events?customerId=${encodeURIComponent(customerId)}`)
      if (res.ok) {
        const data = await res.json() as WebhookEvent[]
        setEvents(data)
      }
    } catch { /* ignore */ }
  }

  async function clearEvents() {
    if (!customerId) return
    setClearing(true)
    try {
      await fetch(`/api/webhook/frankieone/events?customerId=${encodeURIComponent(customerId)}`, { method: 'DELETE' })
      setEvents([])
      setExpandedId(null)
    } catch { /* ignore */ } finally { setClearing(false) }
  }

  async function manualRefresh() {
    setLoading(true)
    await fetchEvents()
    setLoading(false)
  }

  useEffect(() => {
    void fetchEvents()
    pollingRef.current = setInterval(() => { void fetchEvents() }, 5000)
    return () => { if (pollingRef.current) clearInterval(pollingRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  function copyUrl() {
    if (!webhookUrl) return
    void navigator.clipboard.writeText(webhookUrl)
    setCopiedUrl(true)
    setTimeout(() => setCopiedUrl(false), 2000)
  }

  function outcomeColor(event: WebhookEvent): string {
    const t = String(event.eventType ?? '').toLowerCase()
    const result = String(event.payload?.result ?? event.payload?.workflowResult?.result ?? '').toUpperCase()
    if (result === 'PASS' || t.includes('pass') || t.includes('success')) return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
    if (result === 'FAIL' || t.includes('fail') || t.includes('block')) return 'text-red-400 bg-red-500/10 border-red-500/30'
    if (result === 'REVIEW' || t.includes('review')) return 'text-amber-400 bg-amber-500/10 border-amber-500/30'
    return 'text-violet-400 bg-violet-500/10 border-violet-500/30'
  }

  return (
    <div className="space-y-3">
      {/* Endpoint info */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Radio size={13} className="text-orange-400" />
          <p className="text-[11px] font-semibold text-[var(--text-primary)]">Webhook Receiver</p>
          {webhookUrl && (
            <span className="ml-auto flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Listening
            </span>
          )}
        </div>
        <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
          Each customer gets their own webhook URL based on their Customer ID — so events from different customers are kept separate. Enter your credentials in the panel on the left to generate your URL.
        </p>
        {!webhookUrl ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
            <p className="text-[10px] text-amber-400">Enter your Customer ID in the credentials panel to generate your webhook URL.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 py-2">
              <code className="text-[10px] font-mono text-orange-400 flex-1 break-all">{webhookUrl}</code>
              <button onClick={copyUrl} className="shrink-0 flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                {copiedUrl ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                {copiedUrl ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-1">
              <p className="text-[10px] font-semibold text-amber-400">How to configure</p>
              <ol className="list-decimal list-inside space-y-0.5 text-[10px] text-[var(--text-muted)] pl-1">
                <li>Copy the URL above</li>
                <li>Go to FrankieOne portal → Settings → Webhooks (or email help@frankieone.com)</li>
                <li>Add this URL as a webhook endpoint for your UAT environment</li>
                <li>Trigger a workflow and watch events appear below</li>
              </ol>
            </div>
          </>
        )}
      </Card>

      {/* JWT info */}
      <Card className="p-4 space-y-2">
        <p className="text-[11px] font-semibold text-[var(--text-secondary)]">JWT Verification (optional)</p>
        <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
          FrankieOne can sign webhook payloads with a JWT in the <code className="font-mono text-orange-400 text-[9px]">Authorization: Bearer</code> header using RSA-4096. To enable, contact <span className="font-mono text-orange-400">support@frankieone.com</span> — they will provide a customer-specific public key.
        </p>
        <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
          When a JWT is present, the decoded payload is shown in each event below. The <code className="font-mono text-orange-400 text-[9px]">iss</code> claim should be <code className="font-mono text-orange-400 text-[9px]">io.frankiefinancial.kycaml</code>. Signature verification requires the public key from FrankieOne — the display here is decode-only.
        </p>
      </Card>

      {/* Events list */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
          <div className="flex items-center gap-2">
            <p className="text-[11px] font-semibold text-[var(--text-secondary)]">Incoming Events</p>
            {events.length > 0 && <span className="px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-400 text-[9px] font-mono">{events.length}</span>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={manualRefresh} disabled={loading} className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
              <RefreshCw size={10} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            {events.length > 0 && (
              <button onClick={clearEvents} disabled={clearing} className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-red-400 transition-colors">
                <Trash2 size={10} /> Clear all
              </button>
            )}
          </div>
        </div>
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-[var(--text-muted)]">
            <Radio size={24} className="opacity-30" />
            <p className="text-[11px] italic">
              {!customerId ? 'Enter credentials to see your webhook events' : 'No events yet — configure the webhook endpoint above and trigger a workflow'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-color)]">
            {events.map(ev => {
              const isExpanded = expandedId === ev.id
              const colorCls = outcomeColor(ev)
              const result = String(ev.payload?.result ?? ev.payload?.workflowResult?.result ?? ev.eventType ?? 'EVENT').toUpperCase()
              return (
                <div key={ev.id} className="p-3 space-y-2">
                  <button className="w-full flex items-center gap-2 text-left" onClick={() => setExpandedId(isExpanded ? null : ev.id)}>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-mono font-semibold border ${colorCls}`}>{result}</span>
                    {ev.eventType && <span className="text-[9px] font-mono text-[var(--text-muted)]">{ev.eventType}</span>}
                    {ev.entityId && <span className="text-[9px] font-mono text-[var(--text-secondary)]">{ev.entityId}</span>}
                    <span className="ml-auto text-[9px] text-[var(--text-muted)] shrink-0">{new Date(ev.receivedAt).toLocaleString()}</span>
                    {ev.jwtDecoded && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 border border-orange-500/30">JWT</span>}
                    {isExpanded ? <ChevronUp size={11} className="text-[var(--text-muted)] shrink-0" /> : <ChevronDown size={11} className="text-[var(--text-muted)] shrink-0" />}
                  </button>
                  {isExpanded && (
                    <div className="space-y-2">
                      {ev.jwtDecoded && (
                        <div className="bg-orange-500/8 border border-orange-500/25 rounded-lg p-2 space-y-1">
                          <p className="text-[9px] font-semibold text-orange-400 uppercase tracking-wide">JWT Decoded Payload</p>
                          <pre className="text-[9px] font-mono text-[var(--text-secondary)] overflow-auto max-h-32">{JSON.stringify(ev.jwtDecoded, null, 2)}</pre>
                        </div>
                      )}
                      {Object.keys(ev.headers).length > 0 && (
                        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-2 space-y-1">
                          <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Headers</p>
                          {Object.entries(ev.headers).map(([k, v]) => (
                            <div key={k} className="flex gap-2 text-[9px] font-mono">
                              <span className="text-[var(--text-muted)] shrink-0">{k}:</span>
                              <span className="text-[var(--text-secondary)] break-all">{k.toLowerCase() === 'authorization' ? v.slice(0, 30) + '…' : v}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-2 space-y-1">
                        <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Payload</p>
                        <pre className="text-[9px] font-mono text-[var(--text-secondary)] overflow-auto max-h-96">{JSON.stringify(ev.payload, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}

const TABS = [
  { id: 'kyc', label: 'KYC v2 API', icon: Shield, color: 'text-indigo-400' },
  { id: 'kyc-v1', label: 'KYC v1 API', icon: Shield, color: 'text-cyan-400' },
  { id: 'kyb', label: 'KYB v2 API', icon: Building2, color: 'text-emerald-400' },
  { id: 'kyb-v1', label: 'KYB v1 API', icon: Building2, color: 'text-teal-400' },
  { id: 'hosted', label: 'Hosted v2', icon: Fingerprint, color: 'text-violet-400' },
  { id: 'hosted-v1', label: 'Hosted v1', icon: Fingerprint, color: 'text-fuchsia-400' },
  { id: 'embedded', label: 'Embedded OneSDK', icon: IdCard, color: 'text-blue-400' },
  { id: 'webhooks', label: 'Webhooks', icon: Radio, color: 'text-orange-400' },
] as const

type TabId = typeof TABS[number]['id']

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ApiConsole() {
  const [creds, setCreds] = useState<Creds>(DEFAULT_CREDS)
  const [activeTab, setActiveTab] = useState<TabId>('kyc')
  const [log, setLog] = useState<LogEntry[]>([])
  const logIdRef = useRef(0)

  useEffect(() => {
    try {
      const stored = localStorage.getItem('ft-credentials-dealhub')
      if (stored) setCreds({ ...DEFAULT_CREDS, ...JSON.parse(stored) as Partial<Creds> })
    } catch { /* ignore */ }
  }, [])

  function updateCreds(next: Creds) {
    setCreds(next)
    try { localStorage.setItem('ft-credentials-dealhub', JSON.stringify(next)) } catch { /* ignore */ }
  }

  const addLog = useCallback((entry: Omit<LogEntry, 'id' | 'ts'>) => {
    logIdRef.current += 1
    const id = logIdRef.current
    const ts = new Date().toLocaleTimeString()
    setLog(prev => [...prev, { ...entry, id, ts }])
  }, [])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-4 items-start">
        {/* Left sidebar */}
        <div className="space-y-3 xl:sticky xl:top-4">
          <CredentialsPanel creds={creds} onChange={updateCreds} />
          <div className="border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] overflow-hidden">
            <div className="px-3 py-2 border-b border-[var(--border-color)]">
              <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Products</p>
            </div>
            {TABS.map(t => {
              const Icon = t.icon; const active = activeTab === t.id
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left border-b border-[var(--border-color)] last:border-0 transition-colors ${active ? 'bg-[var(--bg-primary)]' : 'hover:bg-[var(--bg-tertiary)]'}`}>
                  <Icon size={14} className={active ? t.color : 'text-[var(--text-muted)]'} />
                  <span className={`text-[12px] font-medium ${active ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>{t.label}</span>
                  {active && <span className="ml-auto w-1 h-4 rounded-full bg-current opacity-30" />}
                </button>
              )
            })}
          </div>
          {/* Log — always visible below products */}
          <Card className="overflow-hidden" style={{ height: '320px' }}>
            <LogPanel log={log} onClear={() => setLog([])} />
          </Card>
        </div>
        {/* Main content */}
        <div className="min-w-0">
          {activeTab === 'kyc' && <KycTab creds={creds} addLog={addLog} />}
          {activeTab === 'kyc-v1' && <KycV1Tab creds={creds} addLog={addLog} />}
          {activeTab === 'kyb' && <KybTab creds={creds} addLog={addLog} />}
          {activeTab === 'kyb-v1' && <KybV1Tab creds={creds} addLog={addLog} />}
          {activeTab === 'hosted' && <HostedTab creds={creds} addLog={addLog} />}
          {activeTab === 'hosted-v1' && <HostedV1Tab creds={creds} addLog={addLog} />}
          {activeTab === 'embedded' && <EmbeddedTab creds={creds} addLog={addLog} />}
          {activeTab === 'webhooks' && <WebhooksTab creds={creds} />}
        </div>
      </div>
    </div>
  )
}
